/* 一般外科 LEVEL 3 — 門診：只是來排刀（acute cholangitis → Reynolds pentad if missed; ERCP before cholecystectomy） */
import {
  addWait,
  ask,
  C,
  type Cell,
  chat,
  type ChoiceItem,
  clearWait,
  clock,
  closeDialog,
  closeZoom,
  type Ctx,
  dither,
  doc,
  drawEcg,
  type DrawFn,
  ent,
  eventBanner,
  findEnt,
  finding,
  getImagery,
  focusInput,
  type Level,
  leaveBy,
  me,
  mkEnt,
  note,
  obox,
  openMonitor,
  openZoom,
  pell,
  pline,
  playEnding,
  portraitCanvas,
  post,
  ptext,
  R,
  rebuildDyn,
  session,
  setZBar,
  spawnAt,
  spend,
  strList,
  T,
  walkEnt,
  zone,
  tr,
  type ZoomCfg,
} from "../../engine";
import { bedHots, drawBedScene } from "../shared/bedside";
import { photoOnly } from "../shared/imaging";

/* ---------- static data ---------- */

const FACTS = {
  ruq: tr(
    "右上腹悶痛三天，比以前膽結石發作痛得久，吃止痛藥沒用",
    "Dull RUQ pain for three days, lasting longer than his past gallstone attacks; painkillers haven't helped",
  ),
  fever: tr(
    "昨晚發冷發抖，蓋兩條棉被還在抖，太太量耳溫 39.2 度",
    "Chills and shaking last night, still shaking under two quilts; his wife took his ear temperature: 39.2",
  ),
  apap: tr(
    "今天早上八點吃了兩顆普拿疼才出門",
    "Took two Panadol (acetaminophen) at 8 this morning before leaving home",
  ),
  urine: tr("這兩天尿像濃茶一樣", "Urine has looked like strong tea for the past two days"),
  stool: tr("大便顏色變淡，有點灰白", "Stools have gone pale, kind of grayish-white"),
  itch: tr("全身皮膚癢，晚上抓到睡不著", "Itchy all over; scratches so much at night he can't sleep"),
  eyes: tr(
    "太太前天就說他眼睛黃黃的，他以為是曬太陽",
    "His wife said two days ago that his eyes looked yellow; he figured it was from being out in the sun",
  ),
  vomit: tr("今天早上吐了一次，沒什麼胃口", "Vomited once this morning, not much appetite"),
  aspirin: tr(
    "三年前心導管放支架，每天吃一顆阿斯匹靈",
    "Had a cardiac cath with a stent three years ago; takes one aspirin a day",
  ),
  meal: tr("中午十二點半吃了半個雞腿便當", "Ate half a chicken-leg bento at 12:30"),
  work: tr(
    "開計程車，一天沒開就少一天收入，四點要去載固定的客人",
    "Drives a taxi; every day off is a day's income lost; has a regular passenger to pick up at 4",
  ),
  hidden: tr(
    "叫太太不要講發燒的事，怕刀被延後",
    "Told his wife not to mention the fever, afraid the surgery would be postponed",
  ),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

/** Order id → [label, minutes until ready]. */
const ORD = {
  cbc: [tr("CBC／DC、CRP", "CBC/DC, CRP"), 25],
  lft: [tr("肝功能與膽紅素（AST/ALT、T/D-bil、ALP、γ-GT）", "LFTs & bilirubin (AST/ALT, T/D-bil, ALP, GGT)"), 25],
  chem: [tr("BUN／Cr、電解質、Lipase", "BUN/Cr, lytes, lipase"), 25],
  lactate: ["Lactate", 10],
  coag: [tr("PT/INR、aPTT", "PT/INR, aPTT"), 25],
  bcx: [tr("血液培養兩套", "Blood cultures ×2"), 2],
  ua: [tr("尿液常規", "UA"), 15],
  us: [tr("腹部超音波", "Abdominal US"), 15],
  ct: [tr("腹部 CT（含顯影劑）", "CT abdomen (contrast)"), 50],
  iv: [tr("打 IV 留置針", "Start IV"), 3],
  fluid: [tr("LR 1000 mL 輸注", "LR 1000 mL infusion"), 3],
  abx: [tr("抗生素：Ceftriaxone 2 g IV", "Abx: ceftriaxone 2 g IV"), 4],
  npo: [tr("禁食禁水", "NPO"), 1],
  apap: [tr("退燒：Acetaminophen", "Antipyretic: acetaminophen"), 3],
} as const satisfies Record<string, readonly [string, number]>;
type OrderId = keyof typeof ORD;
const isOrder = (x: string): x is OrderId => x in ORD;
/** Orders that need him lying on the stretcher with an IV. */
const TREATMENT: readonly OrderId[] = ["iv", "fluid", "abx"];

interface Vitals {
  temp: number;
  sbp: number;
  dbp: number;
  hr: number;
  rr: number;
  spo2: number;
}
interface Order {
  at: number;
  ready: number;
  done: boolean;
}
/** pre 預診室, wait 候診區, tx 治療室推床, wc 廁所, away 離開醫院, gone 送去病房。 */
type PtLoc = "pre" | "wait" | "tx" | "wc" | "away" | "gone";
type Ercp = "none" | "tomorrow" | "urgent";
type Grade = "good" | "ok" | "poor";
const isGrade = (x: unknown): x is Grade => x === "good" || x === "ok" || x === "poor";

interface Vars {
  revealed: Set<FactId>;
  orders: Partial<Record<OrderId, Order>>;
  vitalsSeen: { t: number; v: Vitals; what: "temp" | "bp" | "all" }[];
  triageRead: boolean;
  emrRead: boolean;
  ptLoc: PtLoc;
  /** Where to return to after the toilet. */
  wcBack: { t: number; loc: PtLoc } | null;
  awayAt: number | null;
  toldStay: boolean;
  npoTold: boolean;
  /** abx asked for before cultures: the nurse warned once. */
  bcxWarned: boolean;
  abxBeforeCx: boolean;
  fluidAt: number | null;
  abxAt: number | null;
  apapAt: number | null;
  rigor: boolean;
  shockAt: number | null;
  helpCalled: boolean;
  shockActs: string[];
  resusFluid: boolean;
  o2: boolean;
  vsTalked: boolean;
  vsAccepted: boolean;
  acceptAt: number | null;
  vsBrevity: Grade[];
  vsComing: number | null;
  vsHere: boolean;
  /** 阿凱 came down from the OR between cases. */
  rState: "none" | "here" | "left";
  choleAgreed: boolean;
  choleRejected: boolean;
  giCalls: number;
  giSbar: Grade[];
  ercp: Ercp;
  ercpAt: number | null;
  transportAt: number | null;
  endAt: number | null;
  wifeAsked: boolean;
}

const S = session<Vars>();
const DOOR: Cell = [10, 11];
const PRE: Cell = [2, 2];
const WAIT: Cell = [9, 8];
const STRETCHER: Cell = [13, 2];
const TRIAGE: Vitals = { temp: 38.4, sbp: 126, dbp: 78, hr: 98, rr: 18, spo2: 97 };

/* ---------- patient physiology ---------- */

/** Minute the patient goes into septic shock (Reynolds pentad); Infinity once stabilised in time. */
function shockMinute(): number {
  if (S.shockAt !== null) return S.shockAt;
  if (S.awayAt !== null) return Math.max(S.awayAt + 14, 36);
  const f = S.fluidAt;
  const a = S.abxAt;
  if (f !== null && a !== null && Math.max(f, a) <= 34) return Infinity;
  return 42 + (f !== null && f < 42 ? 10 : 0) + (a !== null && a < 42 ? 8 : 0);
}

function vitals(): Vitals {
  const t = S.t;
  let temp = t < 10 ? 38.4 + 0.02 * t : t < 28 ? 38.6 + (t - 10) * 0.05 : 39.5;
  if (S.apapAt !== null && t - S.apapAt < 45) temp -= 0.8 * Math.min(1, (t - S.apapAt) / 15);
  let sbp = 122 - 0.3 * t;
  let dbp = 74 - 0.22 * t;
  let hr = 102 + 0.35 * t;
  let rr = t > 25 ? 22 : 20;
  let spo2 = 97;
  if (S.fluidAt !== null && S.shockAt === null) {
    const k = Math.min(1, (t - S.fluidAt) / 10);
    sbp += 8 * k;
    dbp += 4 * k;
    hr -= 10 * k;
  }
  const sm = shockMinute();
  if (S.shockAt !== null) {
    const dt = t - S.shockAt;
    const fix = S.resusFluid ? Math.min(dt, 10) : 0;
    temp = 39.7;
    sbp = 80 + fix * 1.6 - (S.resusFluid ? 0 : Math.min(dt, 10) * 0.6);
    dbp = 46 + fix * 0.8;
    hr = 130 - fix * 0.8;
    rr = 26;
    spo2 = S.o2 ? 98 : 93;
  } else if (Number.isFinite(sm) && t > sm - 10) {
    const k = t - (sm - 10);
    sbp -= k * 2.6;
    dbp -= k * 1.5;
    hr += k * 1.3;
    rr = 24;
  }
  return {
    temp: Math.round(temp * 10) / 10,
    sbp: Math.round(sbp),
    dbp: Math.round(dbp),
    hr: Math.round(hr),
    rr,
    spo2,
  };
}

const vtxt = (v: Vitals): string =>
  tr(
    `耳溫 ${v.temp.toFixed(1)}°C，BP ${v.sbp}/${v.dbp}，HR ${v.hr}，RR ${v.rr}，SpO₂ ${v.spo2}%`,
    `Ear temp ${v.temp.toFixed(1)}°C, BP ${v.sbp}/${v.dbp}, HR ${v.hr}, RR ${v.rr}, SpO₂ ${v.spo2}%`,
  );

const preShock = (): boolean => S.shockAt === null && Number.isFinite(shockMinute()) && S.t > shockMinute() - 10;
const jaundiced = { s: "#dcc07a", S: "#b89a52" };
/** Finding names that are also read back as keys. */
const FN = {
  sclera: tr("鞏膜", "Sclera"),
  skin: tr("皮膚", "Skin"),
  abd: tr("腹部", "Abdomen"),
  usImg: tr("超音波影像", "US image"),
};

function condition(): string {
  if (S.shockAt !== null)
    return S.resusFluid && S.t - S.shockAt > 6
      ? tr(
          "剛從休克被救回來一點，意識還是混亂，答非所問，一直說要去開車",
          "Just pulled back a little from shock; still confused, answers don't match the questions, keeps saying he has to go drive",
        )
      : tr(
          "休克中，意識混亂，叫名字會睜眼但答非所問，只會含糊地喊冷",
          "In shock and confused; opens his eyes to his name but his answers make no sense, only mumbles that he's cold",
        );
  if (preShock())
    return tr(
      "反應變慢、講話有點顛三倒四，一直發抖喊冷，但還是嘴硬說沒事",
      "Slower to respond, speech a bit muddled, keeps shivering and saying he's cold, but still insists he's fine",
    );
  if (S.t > 22)
    return tr(
      "開始發冷發抖，臉色很差，但還是說「休息一下就好」",
      "Starting to get chills and shakes, looks terrible, but still says 'I just need a little rest'",
    );
  return tr(
    "精神還可以，有點疲倦，右上腹悶痛，一直想趕快排好刀去開車",
    "Reasonably alert, a bit tired, dull RUQ pain; keen to get the surgery booked and get back to driving",
  );
}

function known(): string {
  const out: string[] = [];
  out.push(
    tr(
      `分診單：${S.triageRead ? `看過（13:40 ${vtxt(TRIAGE)}）` : "沒看"}`,
      `Triage slip: ${S.triageRead ? `read (13:40 ${vtxt(TRIAGE)})` : "not read"}`,
    ),
  );
  out.push(
    tr(
      `自己量的生命徵象：${S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("；") || "沒有"}`,
      `Vitals the clerk took: ${S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `看過舊病歷：${S.emrRead ? "是（上月超音波：膽囊多顆結石、總膽管 6 mm；上月肝功能正常，T-bil 0.7）" : "否"}`,
      `Old chart reviewed: ${S.emrRead ? "yes (last month's US: multiple gallstones, CBD 6 mm; last month's LFTs normal, T-bil 0.7)" : "no"}`,
    ),
  );
  out.push(
    tr(
      `檢查與結果：${S.findings.map((f) => `${clock(f.t)} ${f.name}：${f.text}`).join("；") || "無"}`,
      `Exam and test results: ${S.findings.map((f) => `${clock(f.t)} ${f.name}: ${f.text}`).join("; ") || "none"}`,
    ),
  );
  const pend = (Object.entries(S.orders) as [OrderId, Order][]).filter(([, o]) => !o.done).map(([id]) => ORD[id][0]);
  out.push(tr(`已開未出：${pend.join("、") || "無"}`, `Ordered, pending: ${pend.join(", ") || "none"}`));
  out.push(
    tr(
      `問出的病史：${[...S.revealed].map((id) => FACTS[id]).join("；") || "無"}`,
      `History obtained: ${[...S.revealed].map((id) => FACTS[id]).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `處置：${
        [
          S.fluidAt !== null ? `輸液（${clock(S.fluidAt)}）` : "",
          S.abxAt !== null ? `抗生素（${clock(S.abxAt)}${S.abxBeforeCx ? "，在血液培養之前" : ""}）` : "",
          S.orders.npo ? "禁食" : "",
        ]
          .filter(Boolean)
          .join("、") || "無"
      }`,
      `Treatment: ${
        [
          S.fluidAt !== null ? `IV fluids (${clock(S.fluidAt)})` : "",
          S.abxAt !== null ? `antibiotics (${clock(S.abxAt)}${S.abxBeforeCx ? ", before blood cultures" : ""})` : "",
          S.orders.npo ? "NPO" : "",
        ]
          .filter(Boolean)
          .join(", ") || "none"
      }`,
    ),
  );
  out.push(
    tr(
      `照會消化內科：${S.giCalls ? `打過 ${S.giCalls} 次，ERCP ${ERCP_LABEL[S.ercp]}` : "還沒打"}`,
      `GI consult: ${S.giCalls ? `called ${S.giCalls} time(s), ERCP ${ERCP_LABEL[S.ercp]}` : "not called yet"}`,
    ),
  );
  if (S.shockAt !== null)
    out.push(
      tr(
        `休克：${clock(S.shockAt)} 發生；clerk 的處置：${S.shockActs.join("、") || "無"}`,
        `Shock: occurred at ${clock(S.shockAt)}; clerk's actions: ${S.shockActs.join(", ") || "none"}`,
      ),
    );
  return out.join("\n");
}

const ERCP_LABEL: Record<Ercp, string> = tr(
  { none: "還沒排", tomorrow: "排明天", urgent: "今天急做" },
  { none: "not booked", tomorrow: "tomorrow", urgent: "urgent today" },
);

const reveal = (ids: unknown): void => {
  for (const id of strList(ids)) if (isFact(id)) S.revealed.add(id);
};

/** Tokyo Guidelines 2018 pieces the clerk actually has in hand. */
function tokyo(): { a: boolean; b: boolean; c: boolean } {
  const done = (id: OrderId): boolean => !!S.orders[id]?.done;
  const febrile = S.vitalsSeen.some((x) => x.v.temp >= 38) || S.triageRead || S.revealed.has("fever");
  return {
    a: febrile || done("cbc"),
    b: done("lft") || S.findings.some((f) => f.name === FN.sclera),
    c: done("us") || done("ct"),
  };
}

/* ---------- movement ---------- */

const pt = () => ent("huang");
const wife = () => ent("huang_wife");
const ptPresent = (): boolean => S.ptLoc !== "away" && S.ptLoc !== "gone";

function toStretcher(after?: () => void): void {
  const e = pt();
  S.ptLoc = "tx";
  e.fx2 = null;
  walkEnt(e, STRETCHER);
  e.onArrive = () => {
    [e.x, e.y] = STRETCHER;
    e.fx = e.x;
    e.fy = e.y;
    e.fx2 = "down";
    e.face = "down";
    rebuildDyn();
    after?.();
  };
  const w = wife();
  if (!w.hidden) walkEnt(w, [15, 3]);
}

function toWaiting(): void {
  if (S.ptLoc !== "pre") return;
  S.ptLoc = "wait";
  const e = pt();
  e.face = null;
  walkEnt(e, WAIT);
  e.onArrive = () => {
    e.face = "left";
  };
  if (S.dialog === "pt") closeZoom();
}

function leave(): void {
  if (!ptPresent() || S.shockAt !== null || S.ptLoc === "tx") return;
  S.ptLoc = "away";
  S.awayAt = S.t;
  clearWait("leave");
  closeZoom();
  post(
    "pt",
    "narr",
    null,
    tr(
      "黃先生拿起計程車鑰匙：「醫生，我晚上再打電話來問排刀的時間啦！」太太嘆了口氣跟著走了。",
      "Mr. Huang picks up his taxi keys: “Doc, I’ll call tonight to ask when the surgery is!” His wife sighs and follows him out.",
    ),
  );
  leaveBy(pt(), DOOR, () => {
    if (S.dialog === "pt") closeDialog();
  });
  leaveBy(wife(), DOOR, () => {
    if (S.dialog === "wife") closeDialog();
  });
}

function toilet(back: PtLoc): void {
  const e = pt();
  S.wcBack = { t: S.t + 3, loc: back };
  S.ptLoc = "wc";
  e.fx2 = null;
  walkEnt(e, [1, 9]);
  post("pt", "narr", null, tr("黃先生拿著尿杯去廁所。", "Mr. Huang takes the urine cup to the restroom."));
}

/* ---------- orders ---------- */

function order(id: OrderId, force = false): void {
  if (S.orders[id]) return;
  if (S.ptLoc === "away" || S.ptLoc === "gone") {
    post(
      "nurse",
      "npc",
      "opd_nurse",
      S.ptLoc === "away"
        ? tr("學弟，他人已經走了耶。", "He’s already left, Doc.")
        : tr("他已經上病房了，病房會處理。", "He’s up on the ward now. They’ll handle it."),
    );
    return;
  }
  if (id === "abx" && !S.orders.bcx && !S.bcxWarned && !force) {
    S.bcxWarned = true;
    ask(
      "nurse",
      "opd_nurse",
      tr(
        "學弟，血液培養還沒抽喔。要先抽兩套再打抗生素嗎？確定要直接打的話，再開一次給我。",
        "Blood cultures haven’t been drawn yet. Want two sets before the antibiotic? If you really want it given now, order it again.",
      ),
    );
    return;
  }
  if (id === "abx" && !S.orders.bcx) S.abxBeforeCx = true;
  if ((id === "fluid" || id === "abx") && !S.orders.iv) order("iv");
  let eta: number = ORD[id][1];
  if (id === "us") {
    if (S.rState === "here") {
      eta = 12;
      post(
        "nurse",
        "npc",
        "gs_r",
        tr("超音波我來照，推機器過去。", "I’ll do the ultrasound. Bringing the machine over."),
      );
      const r = ent("gs_r");
      const p = pt();
      walkEnt(r, [p.x + 1 <= 18 ? p.x + 1 : p.x - 1, p.y + (S.ptLoc === "tx" ? 1 : 0)]);
    } else {
      eta = 55;
      post(
        "nurse",
        "npc",
        "opd_nurse",
        tr(
          "阿凱在刀房，超音波排檢查室要等一個小時左右喔。",
          "Kai’s in the OR. A formal ultrasound is about an hour’s wait.",
        ),
      );
    }
  }
  if (id === "ct")
    post(
      "nurse",
      "npc",
      "opd_nurse",
      tr("CT 室說前面還有三個，大概五十分鐘。", "CT says three ahead of him, about fifty minutes."),
    );
  S.orders[id] = { at: S.t, ready: S.t + eta, done: false };
  if (id === "npo") S.npoTold = true;
  if (TREATMENT.includes(id) && S.ptLoc !== "tx" && S.shockAt === null && S.ptLoc !== "wc") {
    post(
      "pt",
      "narr",
      null,
      tr(
        "淑惠姐帶黃先生到治療室，讓他躺上推床。",
        "Shu-Hui takes Mr. Huang to the treatment room and gets him onto the stretcher.",
      ),
    );
    toStretcher();
  }
  if (id === "ua" && (S.ptLoc === "pre" || S.ptLoc === "wait") && S.shockAt === null) {
    post("nurse", "npc", "opd_nurse", tr("尿杯給他了。", "I gave him a urine cup."));
    toilet(S.ptLoc);
  }
  if (id === "ua" && S.shockAt !== null)
    post(
      "nurse",
      "npc",
      "opd_nurse",
      tr("他現在這樣，我直接放尿管取尿。", "The state he’s in, I’ll just put in a Foley for the sample."),
    );
}

const PROCEDURE_REPLY: Partial<Record<OrderId, string>> = {
  iv: tr("IV 打好了，20G 在左手。", "IV’s in, 20G, left hand."),
  fluid: tr("LR 1000 開始跑了。", "LR 1000 is running."),
  abx: tr("Ceftriaxone 2 克打進去了。", "Ceftriaxone 2 g is in."),
  npo: tr(
    "跟他講了，不能吃不能喝。他有點不高興，說中午吃很少。",
    "Told him: nothing to eat or drink. He’s not happy, says he barely ate lunch.",
  ),
  apap: tr("退燒藥給了。", "Antipyretic given."),
  bcx: tr("血液培養兩套抽好了，送出去了。", "Two sets of blood cultures drawn and sent."),
};

function results(id: OrderId): string {
  const shocked = S.shockAt !== null;
  return {
    cbc: tr(
      `CBC：WBC 16,800（Seg 88%、Band 7%），Hb 14.1，PLT ${shocked ? "118k" : "156k"}；CRP 15.6 mg/dL`,
      `CBC: WBC 16,800 (Seg 88%, Band 7%), Hb 14.1, PLT ${shocked ? "118k" : "156k"}; CRP 15.6 mg/dL`,
    ),
    lft: tr(
      "AST 188，ALT 214，ALP 356，γ-GT 512，T-bil 4.8，D-bil 3.9 mg/dL（上個月 T-bil 0.7）",
      "AST 188, ALT 214, ALP 356, GGT 512, T-bil 4.8, D-bil 3.9 mg/dL (last month T-bil 0.7)",
    ),
    chem: tr(
      `BUN 26，Cr ${shocked ? "1.9" : "1.4"}（上個月 0.9），Na 134，K 3.6，Lipase 42（正常）`,
      `BUN 26, Cr ${shocked ? "1.9" : "1.4"} (last month 0.9), Na 134, K 3.6, lipase 42 (normal)`,
    ),
    lactate: `Lactate ${shocked ? "4.6" : S.t > 30 ? "2.8" : "2.1"} mmol/L`,
    coag: tr("PT INR 1.2，aPTT 31 秒", "PT INR 1.2, aPTT 31 s"),
    bcx: tr("血液培養兩套已送（結果要兩到三天）", "Blood cultures ×2 sent (results in 2–3 days)"),
    ua: tr(
      "尿液：深茶色，膽紅素 3+，尿膽素原偏低，WBC 0–2，nitrite (−)",
      "UA: dark tea-colored, bilirubin 3+, urobilinogen low, WBC 0–2, nitrite (−)",
    ),
    us: tr(
      "超音波：總膽管擴張 11 mm，遠端看到一顆約 1 cm 結石併後方聲影；肝內膽管輕度擴張；膽囊多顆結石，壁 4 mm，沒有周邊積液",
      "US: CBD dilated to 11 mm with a ~1 cm distal stone and posterior acoustic shadowing; mild intrahepatic ductal dilation; multiple gallstones, GB wall 4 mm, no pericholecystic fluid",
    ),
    ct: tr(
      "CT：總膽管擴張至 12 mm，遠端總膽管約 1 cm 結石；肝內膽管輕度擴張；膽囊多顆結石、壁稍厚；胰臟不腫；沒有肝膿瘍",
      "CT: CBD dilated to 12 mm with a ~1 cm distal CBD stone; mild intrahepatic ductal dilation; multiple gallstones, GB wall mildly thickened; pancreas not swollen; no liver abscess",
    ),
    iv: "",
    fluid: "",
    abx: "",
    npo: "",
    apap: "",
  }[id];
}

function deliver(id: OrderId): void {
  const o = S.orders[id];
  if (!o) return;
  o.done = true;
  const reply = PROCEDURE_REPLY[id];
  if (id === "fluid") S.fluidAt ??= S.t;
  if (id === "abx") S.abxAt ??= S.t;
  if (id === "apap") S.apapAt = S.t;
  if (id !== "bcx" && reply) {
    post("nurse", "npc", "opd_nurse", reply);
    return;
  }
  const text = results(id);
  finding(ORD[id][0], text);
  if (id === "us" && S.rState === "here") {
    post("pt", "result", "gs_r", text);
    post(
      "pt",
      "npc",
      "gs_r",
      tr("CBD 粗成這樣……這不只是膽囊的問題喔。", "A CBD that fat… this isn’t just a gallbladder problem."),
    );
  } else post("nurse", "result", "opd_nurse", text);
}

/* ---------- exam ---------- */

const ex = {
  find: (label: string, name: string, txt: string, cost = 1): void => {
    if (!ptPresent() || S.ptLoc === "wc") {
      note(tr("病人不在這裡。", "The patient isn’t here."), "pt");
      return;
    }
    if (S.findings.some((f) => f.name === name)) {
      note(tr(`剛剛檢查過了：${txt}`, `Already checked: ${txt}`), "pt");
      return;
    }
    me("pt", tr(`（${label}）`, `(${label})`));
    finding(name, txt);
    post("pt", "narr", null, txt);
    spend(cost);
  },
  measure: (what: "temp" | "bp"): void => {
    if (!ptPresent() || S.ptLoc === "wc") {
      note(tr("病人不在這裡。", "The patient isn’t here."), "pt");
      return;
    }
    const v = vitals();
    S.vitalsSeen.push({ t: S.t, v, what });
    me(
      "pt",
      what === "temp" ? tr("（量耳溫）", "(Takes ear temperature)") : tr("（量血壓、心跳）", "(Checks BP and pulse)"),
    );
    post(
      "pt",
      "result",
      null,
      what === "temp"
        ? tr(`耳溫 ${v.temp.toFixed(1)}°C`, `Ear temp ${v.temp.toFixed(1)}°C`)
        : tr(`BP ${v.sbp}/${v.dbp}，HR ${v.hr}，RR ${v.rr}`, `BP ${v.sbp}/${v.dbp}, HR ${v.hr}, RR ${v.rr}`),
    );
    if (what === "temp" && v.temp >= 39 && S.shockAt === null)
      post(
        "pt",
        "npc",
        "huang",
        tr(
          "三十九？耳溫槍壞了吧，我剛在外面曬太陽。",
          "Thirty-nine? That thermometer’s broken. I was just out in the sun.",
        ),
      );
    spend(1);
  },
  eyes: () =>
    ex.find(
      tr("看眼白", "Check sclera"),
      FN.sclera,
      tr(
        "請他往上看：兩側眼白明顯黃染。在日光燈下，皮膚也偏黃，不只是曬黑。",
        "You have him look up: both sclerae are clearly yellow. Under the fluorescent light his skin looks yellow too, not just tanned.",
      ),
    ),
  skin: () =>
    ex.find(
      tr("看皮膚", "Check skin"),
      FN.skin,
      tr(
        "前臂和背上有好幾條抓痕；手掌、胸口的皮膚都偏黃。",
        "Several scratch marks on his forearms and back; the skin of his palms and chest is yellowish.",
      ),
    ),
  abd: () =>
    ex.find(
      tr("壓右上腹", "Palpate RUQ"),
      FN.abd,
      tr(
        "右上腹壓痛明顯，深吸氣時按壓他會停住吸氣（Murphy 徵象可疑陽性）；肝臟下緣在肋緣下兩指、有壓痛。沒有反彈痛，腹部不僵硬。",
        "Marked RUQ tenderness; when you press as he breathes in deeply, he stops mid-breath (Murphy’s sign possibly positive). Liver edge two fingerbreadths below the costal margin, tender. No rebound, abdomen not rigid.",
      ),
      2,
    ),
  mind: (): void => {
    if (!ptPresent() || S.ptLoc === "wc") {
      note(tr("病人不在這裡。", "The patient isn’t here."), "pt");
      return;
    }
    me("pt", tr("（評估意識：問他人、時、地）", "(Assesses orientation: person, time, place)"));
    const txt =
      S.shockAt !== null
        ? tr(
            "叫他名字會睜眼。問他現在在哪裡，他說「在車上……要去載客人」。今天幾號答錯。GCS E3V4M6。",
            "Opens his eyes to his name. Asked where he is, he says “In the car… gotta pick up a fare.” Gets the date wrong. GCS E3V4M6.",
          )
        : preShock()
          ? tr(
              "人、地答得出來，問今天幾號想了很久才答錯一天，講話比剛才慢。",
              "Knows who and where he is; asked the date, he thinks a long time and is off by a day. Speech slower than before.",
            )
          : tr("人、時、地都清楚，對答切題。", "Oriented to person, time and place; answers make sense.");
    finding(tr(`意識（${clock()}）`, `Mental status (${clock()})`), txt);
    post("pt", "narr", null, txt);
    spend(1);
  },
};

/* ---------- shock ---------- */

function shock(): void {
  S.shockAt = S.t;
  const e = pt();
  closeZoom();
  S.wcBack = null;
  if (S.ptLoc === "wc") {
    S.ptLoc = "wait";
    [e.x, e.y] = [2, 10];
    e.fx = e.x;
    e.fy = e.y;
  }
  const dest = e.path.at(-1);
  if (dest) {
    // Collapses where he was heading, not mid-stride.
    [e.x, e.y] = dest;
    e.fx = e.x;
    e.fy = e.y;
  }
  e.path = [];
  e.moving = null;
  e.onArrive = null;
  e.fx2 = "down";
  rebuildDyn();
  clearWait("leave");
  const where = S.ptLoc === "tx" ? tr("治療室", "Treatment room") : tr("候診區", "Waiting area");
  eventBanner(
    tr("敗血性休克", "Septic shock"),
    tr(`${where}・意識混亂、血壓掉`, `${where} · confused, BP dropping`),
    true,
  );
  post(
    "pt",
    "alert",
    "huang_wife",
    S.ptLoc === "tx"
      ? tr(
          "醫生！他怎麼叫不太醒！一直說要去載客人！",
          "Doctor! I can barely wake him! He keeps saying he has to pick up a passenger!",
        )
      : tr(
          "醫生！我先生從椅子上滑下來了！叫他都亂講話！",
          "Doctor! My husband slid off his chair! He’s talking nonsense!",
        ),
  );
  addWait({
    id: "shock",
    text: tr("黃先生休克，意識混亂！", "Mr. Huang is in shock and confused!"),
    urgent: true,
    icon: tr("！", "!"),
    clearOn: "manual",
  });
}

function helpArrives(): void {
  if (S.helpCalled) return;
  S.helpCalled = true;
  const n = ent("opd_nurse");
  const p = pt();
  walkEnt(n, [p.x, p.y + 1 <= 10 ? p.y + 1 : p.y - 1]);
  post(
    "pt",
    "npc",
    "opd_nurse",
    tr("來了！急救車我推過來了！我去叫主任！", "Coming! I’ve got the crash cart! I’ll get Dr. Chiang!"),
  );
  S.vsComing ??= S.t + 3;
}

function shockChoices(): ChoiceItem[] {
  const act = (label: string, id: string, txt: () => string, wrong = false) =>
    C(
      label,
      () => {
        if (S.shockActs.includes(label)) return;
        S.shockActs.push(label);
        me("pt", tr(`（${label}）`, `(${label})`));
        post("pt", wrong ? "alert" : "narr", wrong ? "opd_nurse" : null, txt());
        if (id === "help") helpArrives();
        if (id === "fluid" && S.helpCalled) {
          S.resusFluid = true;
          S.fluidAt ??= S.t;
          S.orders.iv ??= { at: S.t, ready: S.t, done: true };
          S.orders.fluid ??= { at: S.t, ready: S.t, done: true };
        }
        if (id === "abx" && S.helpCalled) {
          S.orders.bcx ??= { at: S.t, ready: S.t, done: true };
          S.abxAt ??= S.t;
          S.orders.abx ??= { at: S.t, ready: S.t, done: true };
        }
        if (id === "o2" && S.helpCalled) S.o2 = true;
        spend(1);
      },
      S.shockActs.includes(label),
    );
  const crew = (): boolean => S.helpCalled;
  return [
    tr("休克處置", "Shock"),
    act(tr("大聲叫人、推急救車", "Call for help, crash cart"), "help", () =>
      tr("你大喊：「淑惠姐！治療室需要幫忙！」", "You shout: “Shu-Hui! I need help in the treatment room!”"),
    ),
    act(tr("讓他平躺、抬高雙腳", "Lay him flat, legs up"), "flat", () =>
      tr(
        "你和黃太太把他放平，抬高雙腳，他的頭靠在你的手上。",
        "You and Mrs. Huang lay him flat and raise his legs; his head rests on your hand.",
      ),
    ),
    act(tr("接監視器", "Attach monitor"), "mon", () => {
      const v = vitals();
      return crew()
        ? tr(`監視器接上：${vtxt(v)}。`, `Monitor on: ${vtxt(v)}.`)
        : tr(
            "監視器在治療室，旁邊還沒有人可以幫你推過來。",
            "The monitor is in the treatment room, and nobody is here yet to wheel it over.",
          );
    }),
    act(tr("給氧氣", "Give O₂"), "o2", () =>
      crew()
        ? tr("淑惠姐遞上非再吸入面罩，15 L/min。", "Shu-Hui hands you a non-rebreather mask, 15 L/min.")
        : tr("氧氣在急救車上，還沒有人推過來。", "The oxygen is on the crash cart, and nobody has brought it yet."),
    ),
    act(tr("兩條大號 IV，LR 1000 mL 快速灌注", "2 large-bore IVs, LR 1000 mL bolus"), "fluid", () =>
      crew()
        ? tr(
            "淑惠姐和你各打一條 18G，LR 加壓袋全速。",
            "You and Shu-Hui each place an 18G; LR on a pressure bag, wide open.",
          )
        : tr("你身上沒有 IV 的東西，要先叫人。", "You have no IV supplies on you. Call for help first."),
    ),
    act(tr("先抽血液培養，馬上給抗生素", "Cultures, then Abx now"), "abx", () =>
      crew()
        ? S.orders.bcx
          ? tr(
              "培養早就抽過了。Ceftriaxone 2 克馬上推進去。",
              "Cultures were drawn earlier. Ceftriaxone 2 g goes in right away.",
            )
          : tr(
              "淑惠姐從 IV 先抽兩套培養，接著把 Ceftriaxone 推進去。",
              "Shu-Hui draws two sets of cultures off the IV, then pushes the ceftriaxone.",
            )
        : tr("藥車還沒推過來。", "The med cart isn’t here yet."),
    ),
    act(tr("驗血糖", "Check glucose"), "sugar", () =>
      crew()
        ? tr(
            "指尖血糖 142 mg/dL，不是低血糖造成的意識改變。",
            "Fingerstick glucose 142 mg/dL. The confusion isn’t from hypoglycemia.",
          )
        : tr("血糖機在護理站。", "The glucometer is at the nurses’ station."),
    ),
    act(tr("記下時間、通知江主任", "Note time, notify Dr. Chiang"), "vs", () =>
      tr(
        `你看了一下錶：${clock(S.shockAt ?? S.t)} 開始。淑惠姐已經跑去診間了。`,
        `You check your watch: onset ${clock(S.shockAt ?? S.t)}. Shu-Hui has already run to the exam room.`,
      ),
    ),
    act(
      tr("給他退燒藥，讓他在椅子上休息", "Antipyretic, rest in chair"),
      "rest",
      () =>
        tr(
          "他血壓 80，是休克，不是累！先躺平、給水分！",
          "His BP is 80. That’s shock, not tired! Lay him flat and get fluids in!",
        ),
      true,
    ),
    act(
      tr("叫阿凱馬上準備開刀切膽囊", "Have Kai prep for chole now"),
      "chole",
      () =>
        tr(
          "現在切膽囊？他這樣上刀會死在台上！先救起來、先引流膽管！",
          "Take the gallbladder out now? He’ll die on the table like this! Resuscitate him and drain the bile duct first!",
        ),
      true,
    ),
    "sep",
    C(tr("量血壓、心跳", "Check BP & pulse"), () => ex.measure("bp")),
    C(tr("評估意識", "Mental status"), ex.mind),
  ];
}

/* ---------- 江主任 & transport ---------- */

function admit(): void {
  S.vsAccepted = true;
  S.acceptAt = S.t;
  eventBanner(tr("收住院", "Admitted"), tr("外科病房・抗生素・輸液・禁食", "Surgical ward · Abx · fluids · NPO"));
  S.orders.bcx ??= { at: S.t, ready: S.t + 2, done: false };
  if (S.abxAt === null && !S.orders.abx) {
    S.orders.abx = { at: S.t, ready: S.t + 5, done: false };
    S.orders.iv ??= { at: S.t, ready: S.t + 3, done: false };
  }
  if (S.fluidAt === null && !S.orders.fluid) S.orders.fluid = { at: S.t, ready: S.t + 4, done: false };
  S.orders.npo ??= { at: S.t, ready: S.t + 1, done: false };
  post(
    "nurse",
    "npc",
    "opd_nurse",
    tr(
      "主任說要收。我先打 IV、抽培養、上抗生素，病房那邊我問床。",
      "Dr. Chiang says admit. I’ll start the IV, draw cultures and hang the antibiotic, and ask the ward for a bed.",
    ),
  );
  if (S.ptLoc !== "tx" && S.shockAt === null && S.ptLoc !== "wc") toStretcher();
}

function transport(): void {
  if (S.ptLoc === "gone") return;
  S.ptLoc = "gone";
  closeZoom();
  post(
    "pt",
    "narr",
    null,
    tr(
      "傳送人員推著推床進來，把黃先生推往外科病房。",
      "Transport wheels a stretcher in and takes Mr. Huang up to the surgical ward.",
    ),
  );
  const e = pt();
  e.fx2 = null;
  leaveBy(e, DOOR);
  const w = wife();
  if (!w.hidden) leaveBy(w, DOOR);
  S.endAt = S.t + 2;
}

/* ---------- endings ---------- */

const ercpLine = (): string =>
  S.ercp === "urgent"
    ? tr(
        "17:30 ERCP。志豪做了乳頭切開，取出一顆一公分的總膽管結石，黃綠色的膿性膽汁湧出來。",
        "ERCP at 17:30. Chih-Hao does a sphincterotomy and pulls out a 1 cm CBD stone; yellow-green pus wells out with the bile.",
      )
    : tr(
        "隔天早上第一台 ERCP，取出一顆一公分的總膽管結石，膽汁混濁。",
        "First ERCP case the next morning: a 1 cm CBD stone comes out, the bile cloudy.",
      );

function endA(): void {
  void playEnding(
    "A",
    tr("先引流", "Drain First"),
    [
      {
        who: "huang_wife",
        text: tr(
          "醫生，多謝你。他那個人，不逼他不會講。",
          "Thank you, Doctor. That man never says a thing unless you push him.",
        ),
      },
      { text: ercpLine() },
      {
        who: "gi_r",
        text: tr("（LINE）學弟，你那個 SBAR 打得不錯。病人退燒了。", "(LINE) Nice SBAR, kid. His fever’s broken."),
      },
      {
        who: "gs_vs",
        text: tr(
          "發炎退了，這次住院就把膽囊拿掉。腹腔鏡，你到時候進來扶鏡頭。",
          "Once the inflammation settles, the gallbladder comes out this admission. Laparoscopic. You’ll be holding the camera.",
        ),
      },
    ],
    () =>
      tr(
        `黃先生在 ${clock(S.acceptAt ?? S.t)} 收住院，ERCP ${ERCP_LABEL[S.ercp]}，沒有在門診休克。幾天後他會回到刀房做腹腔鏡膽囊切除。`,
        `Mr. Huang was admitted at ${clock(S.acceptAt ?? S.t)}, ERCP ${ERCP_LABEL[S.ercp]}, and never went into shock in clinic. In a few days he’ll be back in the OR for a laparoscopic cholecystectomy.`,
      ),
  );
}

function endB(): void {
  void playEnding(
    "B",
    tr("病房的凌晨", "Small Hours on the Ward"),
    [
      {
        text: tr(
          "黃先生上了病房，打了抗生素。但是沒有人排 ERCP。",
          "Mr. Huang goes up to the ward and gets his antibiotics. But nobody books an ERCP.",
        ),
      },
      {
        who: "gs_nurse",
        text: tr(
          "（凌晨兩點的電話）學弟，12 床黃先生血壓 84/50，叫不太醒，你快來！",
          "(Phone call, 2 a.m.) Doc, bed 12, Mr. Huang: BP 84/50, I can barely wake him. Get up here!",
        ),
      },
      {
        text: tr(
          "值班 R 打給消化內科，凌晨三點緊急 ERCP，膿性膽汁從乳頭湧出來。他在加護病房住了四天。",
          "The on-call resident calls GI. Emergency ERCP at 3 a.m.; pus pours out of the papilla. He spends four days in the ICU.",
        ),
      },
      {
        who: "gs_vs",
        text: tr(
          "膽管炎是膿瘍。膿瘍要引流，抗生素只是幫忙。",
          "Cholangitis is an abscess. Abscesses get drained. Antibiotics only help.",
        ),
      },
    ],
    () =>
      tr(
        "黃先生收住院了，但膽道沒有及時引流，半夜進展成敗血性休克，緊急 ERCP 後轉加護病房。",
        "Mr. Huang was admitted, but his bile duct wasn’t drained in time. He went into septic shock overnight and ended up in the ICU after an emergency ERCP.",
      ),
  );
}

function endC(): void {
  const acts = S.shockActs;
  void playEnding(
    "C",
    tr("候診區的休克", "Shock in the Waiting Area"),
    [
      {
        text: tr(
          "推床到了。大家一起把黃先生抬上去，加壓袋擠著第二包 LR。",
          "The stretcher arrives. Everyone lifts Mr. Huang onto it, a pressure bag squeezing in the second bag of LR.",
        ),
      },
      {
        who: "gs_vs",
        text: tr(
          "Levophed 準備，送急診 resuscitation。GI 我打了，血壓穩一點就推去做 ERCP。",
          "Get Levophed ready, take him to the ER resus bay. I’ve called GI. Once his pressure is steadier, he goes for ERCP.",
        ),
        fn: () => {
          const e = pt();
          e.fx2 = null;
          leaveBy(e, DOOR);
          leaveBy(ent("opd_nurse"), DOOR);
        },
      },
      {
        who: "huang_wife",
        text: tr("老公！你不要嚇我！", "Honey! Don’t do this to me!"),
        fn: () => leaveBy(wife(), DOOR),
      },
      {
        who: "gs_vs",
        text: tr(
          "他早上就在發燒、黃疸。這不是候診區該出現的病人。",
          "He’s been febrile and jaundiced since this morning. This patient never belonged in a waiting area.",
        ),
      },
    ],
    () =>
      tr(
        `黃先生在 ${clock(S.shockAt ?? S.t)} 於門診進展成 Reynolds pentad（敗血性休克）。你的處置：${acts.join("、") || "沒有做任何處置"}。緊急 ERCP 後轉加護病房，幾天後才有辦法回來開膽囊。`,
        `At ${clock(S.shockAt ?? S.t)} Mr. Huang progressed to Reynolds pentad (septic shock) in clinic. What you did: ${acts.join(", ") || "nothing at all"}. After an emergency ERCP he went to the ICU; it will be days before he can come back for his gallbladder.`,
      ),
  );
}

function endD(): void {
  void playEnding(
    "D",
    tr("回去開車", "Back to the Taxi"),
    [
      { who: "opd_nurse", text: tr("（接起電話）……急診？黃先生？", "(Picks up the phone) …The ER? Mr. Huang?") },
      {
        who: "opd_nurse",
        text: tr(
          "學弟，你早上那個黃先生，載客人載到一半在路邊停下來，意識不清，乘客叫 119 送急診。血壓 78。",
          "Doc, your Mr. Huang from earlier pulled over in the middle of a fare, confused. His passenger called 119 and he was brought to the ER. BP 78.",
        ),
      },
      {
        who: "gs_vs",
        text: tr(
          "黃疸、發燒、右上腹痛的人，你讓他回去開車？",
          "Jaundice, fever, RUQ pain, and you let him go back to driving?",
        ),
      },
      {
        text: tr(
          "急診緊急 ERCP，膿性膽汁。他在加護病房醒來的第一句話是：「我的車停在哪裡？」",
          "Emergency ERCP from the ER: purulent bile. The first thing he says when he wakes up in the ICU: “Where’d I park my car?”",
        ),
      },
    ],
    () =>
      tr(
        `你在 ${clock(S.awayAt ?? S.t)} 讓黃先生離開門診。他在外面進展成敗血性休克，被救護車送回急診。`,
        `You let Mr. Huang leave the clinic at ${clock(S.awayAt ?? S.t)}. He went into septic shock outside and came back to the ER by ambulance.`,
      ),
  );
}

/* ---------- close-ups ---------- */

function drawSeated(c: Ctx, now: number): void {
  const room = S.ptLoc === "pre";
  R(c, room ? "#e3eaf6" : "#ece6d6", 0, 0, 192, 78);
  for (let x = 0; x < 192; x += 24) R(c, room ? "#d6dff0" : "#e2dac7", x, 0, 1, 78);
  R(c, "#a9b4cf", 0, 76, 192, 3);
  R(c, room ? "#ccd6ea" : "#d9d0bb", 0, 79, 192, 29);
  for (let x = 0; x < 192; x += 16) R(c, room ? "#bcc8e0" : "#cdc3ab", x, 79, 1, 29);
  dither(c, 0, 96, 192, 12, room ? "#b4c0da" : "#c9bea4", 0.4);
  if (room) {
    obox(c, "#b98552", 6, 40, 44, 5);
    R(c, "#8e6238", 6, 45, 44, 2);
    R(c, "#6b4a34", 8, 47, 3, 28);
    R(c, "#6b4a34", 45, 47, 3, 28);
    obox(c, "#20263a", 14, 22, 24, 16);
    R(c, "#5f8fb8", 16, 24, 20, 12);
    R(c, "#9fd0ef", 17, 25, 6, 2);
  } else {
    // waiting-room poster: 膽結石衛教
    obox(c, "#f4f4f6", 10, 10, 34, 40);
    R(c, "#3b8a5a", 12, 12, 30, 5);
    pell(c, 27, 30, 8, 6, "#5aa06a");
    R(c, "#e0b860", 24, 29, 3, 3);
    R(c, "#e0b860", 29, 31, 2, 2);
    R(c, "#9aa0b2", 14, 42, 26, 1);
    R(c, "#9aa0b2", 14, 45, 20, 1);
  }
  obox(c, "#4a6a9a", 70, 22, 52, 44);
  R(c, "#5f86bf", 72, 24, 48, 4);
  obox(c, "#5f86bf", 66, 64, 60, 8);
  R(c, "#3a3f52", 70, 73, 3, 20);
  R(c, "#3a3f52", 119, 73, 3, 20);
  const yellow = S.t > 20 || S.findings.some((f) => f.name === FN.sclera);
  const skin = yellow ? "#dcc07a" : "#dcb082";
  const skinS = yellow ? "#b89a52" : "#ba8458";
  const shirt = "#c8a050";
  const shirtS = "#a07e38";
  obox(c, skin, 64, 46, 10, 22);
  R(c, skinS, 64, 62, 10, 6);
  obox(c, skin, 118, 46, 10, 22);
  R(c, skinS, 118, 62, 10, 6);
  if (S.findings.some((f) => f.name === FN.skin)) {
    pline(c, 120, 50, 125, 58, "#c0504a", 1);
    pline(c, 122, 49, 127, 57, "#c0504a", 1);
    pline(c, 66, 52, 70, 60, "#c0504a", 1);
  }
  if (S.vitalsSeen.some((v) => v.what === "bp")) {
    obox(c, "#3b5fa8", 63, 48, 12, 9);
    R(c, "#5f86bf", 64, 49, 10, 2);
    pline(c, 68, 57, 60, 76, "#20263a", 1);
  }
  // 計程車司機的格子襯衫
  obox(c, shirt, 76, 48, 40, 26);
  for (let x = 78; x < 114; x += 6) R(c, shirtS, x, 49, 1, 24);
  for (let y = 52; y < 72; y += 6) R(c, shirtS, 77, y, 38, 1);
  R(c, "#f4f4f6", 94, 48, 4, 8);
  if (S.findings.some((f) => f.name === FN.abd)) obox(c, "#efc8a0", 98, 56, 10, 6);
  obox(c, "#3a3f52", 82, 76, 12, 16);
  obox(c, "#3a3f52", 98, 76, 12, 16);
  obox(c, "#2a2230", 80, 92, 15, 6);
  obox(c, "#2a2230", 97, 92, 15, 6);
  c.drawImage(portraitCanvas("huang", yellow ? jaundiced : {}), 72, 2, 48, 48);
  if (S.findings.some((f) => f.name === FN.sclera) || S.t > 30) {
    // icteric sclera next to each eye (portrait scaled 3×)
    for (const gx of [4, 6, 9, 11]) R(c, "#f0d84a", 72 + gx * 3, 2 + 7 * 3, 3, 3);
  }
  const shiver = S.t > 22 && Math.floor(now / 90) % 2;
  if (shiver) R(c, skinS, 75, 50, 1, 6);
  if (Math.floor(now / 1700) % 6 === 0) R(c, "#2a2428", 84, 23, 24, 2);
  // 計程車鑰匙
  obox(c, "#e0c060", 124, 60, 5, 4);
  R(c, "#9aa0b2", 127, 63, 1, 4);
}

function seatedZoom(): ZoomCfg {
  return {
    title: tr("黃先生", "Mr. Huang"),
    sub: tr("點身體的部位來檢查，點臉跟他說話", "Tap a body part to examine, his face to talk"),
    draw: drawSeated,
    hot: [
      {
        x: 80,
        y: 4,
        w: 32,
        h: 18,
        label: tr("臉（跟他說話）", "Face (talk to him)"),
        fn: () => {
          closeZoom();
          focusInput();
        },
      },
      { x: 80, y: 22, w: 32, h: 8, label: tr("眼睛（看眼白）", "Eyes (sclera)"), fn: ex.eyes },
      { x: 112, y: 22, w: 8, h: 14, label: tr("耳朵（量耳溫）", "Ear (ear temp)"), fn: () => ex.measure("temp") },
      { x: 62, y: 44, w: 14, h: 26, label: tr("手臂（量血壓、心跳）", "Arm (BP & pulse)"), fn: () => ex.measure("bp") },
      { x: 116, y: 44, w: 14, h: 18, label: tr("前臂皮膚（看抓痕、膚色）", "Forearm (scratches, color)"), fn: ex.skin },
      { x: 96, y: 52, w: 18, h: 14, label: tr("右上腹（壓痛、Murphy）", "RUQ (tenderness, Murphy)"), fn: ex.abd },
      { x: 78, y: 66, w: 18, h: 8, label: tr("跟他聊聊（評估意識）", "Chat (mental status)"), fn: ex.mind },
    ],
  };
}

function bedZoom(): ZoomCfg {
  return {
    title: tr("治療室推床", "Treatment-room stretcher"),
    sub: tr("點監視器、肚子、點滴……", "Tap the monitor, belly, IV…"),
    draw: (c: Ctx, now: number) => {
      const v = vitals();
      drawBedScene(c, now, {
        wall: "#e3efe4",
        wall2: "#cadfcc",
        base: "#a9b8a4",
        floor: "#d6e7d8",
        floor2: "#c2d6c4",
        pt: "huang",
        pale: S.shockAt !== null ? { s: "#d8cc98", S: "#b8aa78" } : jaundiced,
        blanket: "#e8e8f0",
        blanket2: "#c9ccd8",
        shiver: S.t > 22,
        iv: !!S.orders.iv,
        iv2: S.fluidAt !== null ? "#e6f2f8" : undefined,
        mask: S.o2,
        companion: wife().hidden ? undefined : "huang_wife",
        monitor: (mc, mnow) => {
          drawEcg(mc, 10, 15, 26, 6, v.hr, mnow, "#3be37a");
          ptext(mc, v.hr, 37, 9, "#3be37a");
          ptext(mc, v.spo2, 37, 21, "#4fd6ff");
          if ((v.hr > 120 || v.sbp < 90) && Math.floor(mnow / 400) % 2) obox(mc, "#ff3b4e", 47, 7, 3, 3);
        },
      });
    },
    hot: () =>
      bedHots({
        onMonitor: openMon,
        ptLabel: tr("黃先生（跟他說話）", "Mr. Huang (talk to him)"),
        bellyLabel: tr("右上腹", "RUQ"),
        onBelly: () =>
          setZBar(
            [
              C(tr("壓右上腹", "Palpate RUQ"), ex.abd),
              C(tr("看眼白", "Check sclera"), ex.eyes),
              C(tr("看皮膚", "Check skin"), ex.skin),
              C(tr("量耳溫", "Ear temp"), () => ex.measure("temp")),
              C(tr("評估意識", "Mental status"), ex.mind),
            ],
            tr("檢查", "Exam"),
          ),
        onIv: () =>
          note(
            S.orders.iv
              ? "IV 20G" +
                  (S.fluidAt !== null ? tr("，LR 在跑", ", LR running") : "") +
                  (S.abxAt !== null ? tr("，抗生素打過了", ", Abx given") : "")
              : tr("還沒打 IV。", "No IV yet."),
            "pt",
          ),
        ...(wife().hidden ? {} : { companion: "huang_wife" }),
        companionLabel: tr("黃太太（跟她說話）", "Mrs. Huang (talk to her)"),
      }),
  };
}

function ptZoom(): ZoomCfg | null {
  if (!ptPresent() || S.ptLoc === "wc") return null;
  if (S.ptLoc === "tx" || S.shockAt !== null) return bedZoom();
  return seatedZoom();
}

function openMon(): void {
  if (S.ptLoc !== "tx" && S.shockAt === null) {
    note(
      tr(
        "監視器在治療室。要先讓他躺到推床上（開 IV、輸液或抗生素，淑惠姐會帶他過去）。",
        "The monitor is in the treatment room. Get him onto the stretcher first (order an IV, fluids or antibiotics and Shu-Hui will take him there).",
      ),
      "pt",
    );
    return;
  }
  const v = vitals();
  S.vitalsSeen.push({ t: S.t, v, what: "all" });
  post("pt", "result", null, vtxt(v), { silent: true });
  openMonitor(
    tr("治療室・監視器", "Treatment room · Monitor"),
    () => {
      const now = vitals();
      const last = S.vitalsSeen.at(-1) ?? { t: S.t, v: now };
      return {
        hr: now.hr,
        spo2: now.spo2,
        rr: now.rr,
        sbp: last.v.sbp,
        dbp: last.v.dbp,
        nibpT: clock(last.t),
        alarm: now.hr > 120 || now.sbp < 90,
      };
    },
    [
      C(tr("重新量 NIBP（1 分鐘）", "Recheck NIBP (1 min)"), () => {
        const nv = vitals();
        S.vitalsSeen.push({ t: S.t, v: nv, what: "all" });
        post("pt", "result", null, vtxt(nv));
        spend(1);
      }),
    ],
    () => {
      if (S.dialog !== "pt") return;
      const z = ptZoom();
      if (z) openZoom({ dlg: "pt", ...z });
    },
  );
  spend(1);
}

/* ---------- ultrasound image ---------- */

/** The RUQ ultrasound as a real image (photo mode only; drawn mode keeps the written report). */
function openUs(dlg: string): void {
  const mark = (txt: string): void => {
    if (S.findings.some((f) => f.name === FN.usImg && f.text === txt)) note(txt, dlg);
    else {
      finding(FN.usImg, txt);
      post(dlg, "narr", null, txt);
    }
  };
  openZoom({
    dlg,
    title: tr("右上腹超音波", "RUQ ultrasound"),
    sub: tr("斜切面・肝門", "Oblique view · porta hepatis"),
    draw: photoOnly,
    photo: {
      id: "us_cbd",
      hot: [
        {
          x: 38,
          y: 70,
          w: 14,
          h: 22,
          label: tr("管子末端那個亮點，後面黑一條", "Bright spot at the end of the tube, dark streak behind it"),
          fn: () =>
            mark(
              tr(
                "總膽管遠端一顆約 1 公分的結石：亮的強回音，後方有聲影（acoustic shadow）",
                "A ~1 cm stone in the distal CBD: bright and echogenic, with posterior acoustic shadowing",
              ),
            ),
        },
        {
          x: 22,
          y: 66,
          w: 40,
          h: 18,
          label: tr("肝門下方那條管子", "The tube below the porta hepatis"),
          fn: () =>
            mark(
              tr(
                "總膽管擴張到 11 mm（正常 6 mm 以下；他上個月才 6 mm）",
                "CBD dilated to 11 mm (normal is 6 mm or less; his was 6 mm just last month)",
              ),
            ),
        },
        {
          x: 0,
          y: 58,
          w: 24,
          h: 30,
          label: tr("左下那個黑色的囊", "The black sac at lower left"),
          fn: () =>
            mark(
              tr(
                "膽囊：裡面是膽汁（黑色無回音），壁 4 mm；這個切面沒切到結石，報告寫多顆結石",
                "Gallbladder: full of bile (black, anechoic), wall 4 mm. This view misses the stones; the report says multiple",
              ),
            ),
        },
        {
          x: 0,
          y: 2,
          w: 71,
          h: 54,
          label: tr("肝臟", "Liver"),
          fn: () =>
            note(
              tr(
                "肝臟回音均勻。肝內膽管輕度擴張要在別的切面看。",
                "Liver echotexture is homogeneous. The mild intrahepatic ductal dilation shows on other views.",
              ),
              dlg,
            ),
        },
      ],
    },
    hint: tr(
      "黑的是液體，白的是硬的；石頭後面會有一條黑影",
      "Black is fluid, white is solid; a stone casts a dark shadow behind it",
    ),
  });
}

/* ---------- dialogs ---------- */

function nurseChoices(): ChoiceItem[] {
  const o = S.orders;
  const btn = (id: OrderId) =>
    C(
      ORD[id][0],
      () => {
        me("nurse", tr(`（${ORD[id][0]}）`, `(${ORD[id][0]})`));
        const wasWarned = S.bcxWarned;
        order(id, id === "abx" && wasWarned);
        spend(1);
      },
      !!o[id],
    );
  return [
    tr("檢驗", "Labs"),
    ...(["cbc", "lft", "chem", "lactate", "coag", "bcx", "ua"] as const).map(btn),
    tr("影像", "Imaging"),
    ...(["us", "ct"] as const).map(btn),
    ...(o.us?.done && getImagery() === "photo" ? [C(tr("看超音波影像", "View US image"), () => openUs("nurse"))] : []),
    tr("處置", "Treatment"),
    ...(["iv", "fluid", "abx", "npo", "apap"] as const).map(btn),
  ];
}

/** zh reads after 在; en reads after "is". */
const LOC_NAME: Record<PtLoc, string> = tr(
  {
    pre: "預診室",
    wait: tr("候診區", "Waiting area"),
    tx: "治療室的推床上",
    wc: tr("廁所", "Restroom"),
    away: "醫院外面",
    gone: "病房",
  },
  {
    pre: "in the pre-exam room",
    wait: "in the waiting area",
    tx: "on the treatment-room stretcher",
    wc: "in the restroom",
    away: "outside the hospital",
    gone: "on the ward",
  },
);

const dialogs: Level["dialogs"] = {
  pt: {
    title: tr("黃先生", "Mr. Huang"),
    sub: () =>
      S.shockAt !== null
        ? tr("休克・意識混亂", "Shock · confused")
        : S.ptLoc === "tx"
          ? tr("治療室推床", "Treatment-room stretcher")
          : tr(`55 歲，計程車司機・${LOC_NAME[S.ptLoc]}`, `55, taxi driver · ${LOC_NAME[S.ptLoc]}`),
    pics: ["huang"],
    ch: "pt",
    talk: () => ptPresent() && S.ptLoc !== "wc",
    ph: tr("對黃先生說…", "Say to Mr. Huang…"),
    choices() {
      if (!ptPresent()) return [];
      if (S.shockAt !== null) return shockChoices();
      return [
        C(tr("特寫", "Close-up"), () => {
          const z = ptZoom();
          if (z) openZoom({ dlg: "pt", ...z });
        }),
        C(tr("量耳溫", "Ear temp"), () => ex.measure("temp")),
        C(tr("量血壓、心跳", "Check BP & pulse"), () => ex.measure("bp")),
        C(tr("看眼白", "Check sclera"), ex.eyes),
        C(tr("看皮膚", "Check skin"), ex.skin),
        C(tr("壓右上腹", "Palpate RUQ"), ex.abd),
        C(tr("評估意識", "Mental status"), ex.mind),
        ...(S.orders.us?.done && getImagery() === "photo"
          ? [C(tr("看超音波影像", "View US image"), () => openUs("pt"))]
          : []),
      ];
    },
    async onTalk(text) {
      await chat(text, {
        ch: "pt",
        think: "huang",
        speakers: { huang: tr("黃先生本人", "Mr. Huang himself") },
        body: tr(
          `你在醫學教育遊戲中扮演「黃先生」，55 歲計程車司機，講話台灣國語夾台語（「啊」「歹勢」「沒要緊啦」），愛面子、怕花錢、很能忍。玩家是外科見習醫學生（clerk），在江主任的門診幫你預診。
上個月健檢超音波看到膽結石，今天是來「排膽囊手術的日期」。他一心只想排好刀、趕快回去開車，覺得自己只是「膽結石又在痛」。被直接問到的症狀不會說謊，但會淡化（「有一點啦」「曬太陽曬的」「普通感冒」）。
【隱藏真相，不能說出】急性膽管炎（總膽管結石），正往敗血性休克走。
【可揭露的事實】只有被問到相關問題才說，括號是 id：
${Object.entries(FACTS)
  .filter(([k]) => k !== "hidden")
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
fever 只有被具體問到「發燒、發冷、發抖、畏寒」才承認，而且會先說「一點點啦」；被追問才講出 39.2 度。apap 只有被問到「今天吃了什麼藥」或「有沒有吃退燒藥」才說。urine 只有被問到小便才說；stool 只有被問到大便才說。aspirin 只有被問到用藥、慢性病、心臟才說。
【行動】act 只能是 none、leave、stay。clerk 同意他先離開（回去開車、去吃東西、先回家等通知）時用 leave（他會真的走掉）；clerk 請他留下、先不要走、要住院時用 stay；其他情況 none。他已經在治療室躺著或已經休克時一律 none。
【狀態】時間 ${clock()}；他在${LOC_NAME[S.ptLoc]}。他現在：${condition()}。${S.npoTold ? "已經被告知禁食。" : ""}已揭露：${[...S.revealed].join(",") || "無"}`,
          `You are playing "Mr. Huang" in a medical education game: a 55-year-old taxi driver who talks in plain, folksy English, full of shrugs and little apologies ("ah, sorry, sorry", "it's nothing, really"); proud, worried about money, very good at toughing things out. The player is a surgery clerk (medical student) doing your pre-exam in Dr. Chiang's clinic.
A health-check ultrasound last month showed gallstones, and today he is here to "book the date for his gallbladder surgery." All he wants is to get the surgery booked and get back to driving; he thinks it's just "the gallstones acting up again." He won't lie about a symptom he is asked about directly, but he plays it down ("just a little", "it's from the sun", "just a cold").
[HIDDEN TRUTH, NEVER SAY IT] Acute cholangitis (common bile duct stone), heading toward septic shock.
[FACTS HE CAN REVEAL] Only when asked a related question; the id is in parentheses:
${Object.entries(FACTS)
  .filter(([k]) => k !== "hidden")
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
fever: he admits it only when asked specifically about "fever, chills, shaking, feeling cold," and first says "just a little"; only when pressed does he give the 39.2. apap: only when asked "what medicine did you take today" or "did you take anything for the fever." urine: only when asked about his urine; stool: only when asked about his bowel movements. aspirin: only when asked about medications, chronic conditions or his heart.
[ACTION] act must be none, leave or stay. Use leave when the clerk agrees he can go for now (go back to driving, go get something to eat, go home and wait to hear) (he will really walk out); use stay when the clerk asks him to stay, not to leave yet, or says he needs to be admitted; otherwise none. Always none if he is already lying in the treatment room or already in shock.
[STATE] Time ${clock()}; he is ${LOC_NAME[S.ptLoc]}. Right now he is: ${condition()}. ${S.npoTold ? "He has been told he is NPO. " : ""}Revealed so far: ${[...S.revealed].join(",") || "none"}`,
        ),
        extra: `"revealed":[],"act":"none"`,
        after(r) {
          reveal(r.revealed);
          if (r.act === "leave") leave();
          else if (r.act === "stay") {
            S.toldStay = true;
            clearWait("leave");
          }
        },
      });
    },
  },
  wife: {
    title: tr("黃太太", "Mrs. Huang"),
    sub: () => (S.ptLoc === "tx" ? tr("在推床旁邊", "By the stretcher") : tr("在候診區", "In the waiting area")),
    pics: ["huang_wife"],
    ch: "wife",
    talk: true,
    ph: tr("對黃太太說…", "Say to Mrs. Huang…"),
    async onTalk(text) {
      S.wifeAsked = true;
      clearWait("wifeq");
      await chat(text, {
        ch: "wife",
        think: "huang_wife",
        speakers: { huang_wife: tr("黃太太", "Mrs. Huang") },
        body: tr(
          `你在醫學教育遊戲中扮演「黃太太」，52 歲，在菜市場賣菜，陪先生來外科門診。講話直、台語夾國語、很擔心但不敢在先生面前多說，因為先生叫她不要講發燒的事。玩家是外科見習醫學生（clerk）。
醫療人員認真問的話，她會講，而且比先生講得具體。
【她知道的事】括號是 id：
- (fever) 昨天半夜他發冷，整張床都在抖，蓋兩條棉被還在抖，她量耳溫 39.2 度
- (apap) 今天早上八點她拿兩顆普拿疼給他吃，所以現在可能沒那麼燒
- (eyes) 前天就發現他眼睛黃黃的，他說是曬太陽
- (urine) 馬桶裡的尿像濃茶
- (itch) 晚上一直抓癢
- (vomit) 早上吐了一次
- (hidden) 他叫我不要講發燒的事，怕刀被延後、怕要住院不能開車
- (work) 他四點要去載一個固定的客人
只有 clerk 問到相關（或問「他最近怎麼樣」）時才說。不要說出任何醫學診斷。
【狀態】時間 ${clock()}；先生在${LOC_NAME[S.ptLoc]}，${condition()}。${S.shockAt !== null ? "她剛看到先生意識不清，非常驚慌，一直哭。" : ""}`,
          `You are playing "Mrs. Huang" in a medical education game: 52, sells vegetables at the market, here with her husband at the surgery clinic. She talks bluntly in plain, down-to-earth English; she is very worried but doesn't dare say much in front of her husband, because he told her not to mention the fever. The player is a surgery clerk (medical student).
If the medical staff ask her seriously, she tells them, and in more concrete detail than her husband.
[WHAT SHE KNOWS] The id is in parentheses:
- (fever) In the middle of last night he had chills, the whole bed was shaking, still shaking under two quilts; she took his ear temperature: 39.2
- (apap) At 8 this morning she gave him two Panadol, so he may not be running as hot right now
- (eyes) Two days ago she noticed his eyes were yellow; he said it was from the sun
- (urine) His urine in the toilet looked like strong tea
- (itch) He scratches all night long
- (vomit) He threw up once this morning
- (hidden) He told me not to mention the fever; he's afraid the surgery will be pushed back, afraid of being admitted and not being able to drive
- (work) He has to pick up a regular passenger at 4
She only says these when the clerk asks about something related (or asks "how has he been lately"). She never states a medical diagnosis.
[STATE] Time ${clock()}; her husband is ${LOC_NAME[S.ptLoc]}, ${condition()}. ${S.shockAt !== null ? "She has just seen her husband confused and barely responsive; she is panicking and keeps crying." : ""}`,
        ),
        extra: `"revealed":[]`,
        after: (r) => reveal(r.revealed),
      });
    },
  },
  nurse: {
    title: tr("淑惠姐", "Shu-Hui"),
    sub: tr("外科門診跟診護理師", "Surgery clinic nurse"),
    pics: ["opd_nurse"],
    ch: "nurse",
    talk: true,
    ph: tr("對淑惠姐說，也可以口頭請她開檢查、處置", "Talk to Shu-Hui, or ask her for tests and treatments"),
    choices: nurseChoices,
    async onTalk(text) {
      await chat(text, {
        ch: "nurse",
        think: "opd_nurse",
        speakers: { opd_nurse: tr("淑惠姐", "Shu-Hui") },
        body: tr(
          `你在醫學教育遊戲中扮演外科門診資深跟診護理師「淑惠姐」，48 歲，跟江主任的診跟了十五年，什麼都看過，對學生嘴巴有點兇但很照顧。玩家是外科見習醫學生（clerk）。
13:40 分診時她幫 12 號黃先生量了耳溫 38.4 度，病人說是剛在外面曬太陽，她就在分診單上寫了一個問號，沒有特別提醒。被問到時會承認：「他說曬太陽，我想說等一下再量一次，後來太忙忘了。」
【clerk 目前知道的】
${known()}
【可執行的事 id】${Object.entries(ORD)
            .map(([k, v]) => `${k} ${v[0]}`)
            .join("；")}
規則：clerk 明確要求的事，把 id 放進 orders（開抗生素前沒抽血液培養，她會先提醒）。門診 clerk 開的單要主任簽，她會說「我先做，等一下請主任補簽」。不要編造還沒出來的結果。不要說出診斷。`,
          `You are playing "Shu-Hui," the senior nurse who works the surgery clinic, in a medical education game. She is 48 and has worked Dr. Chiang's clinic for fifteen years; she has seen everything, is a bit sharp-tongued with students but looks out for them. The player is a surgery clerk (medical student).
At 13:40 triage she took patient #12 Mr. Huang's ear temperature: 38.4. He said he had just been out in the sun, so she wrote a question mark on the triage slip and didn't flag it to anyone. If asked, she admits: "He said it was the sun. I meant to recheck it later, then it got busy and I forgot."
[WHAT THE CLERK KNOWS SO FAR]
${known()}
[ACTION IDS SHE CAN CARRY OUT] ${Object.entries(ORD)
            .map(([k, v]) => `${k} ${v[0]}`)
            .join("; ")}
Rules: for anything the clerk explicitly asks for, put the id in orders (if antibiotics are ordered before blood cultures are drawn, she reminds them first). Orders from a clerk in clinic need the attending's signature; she says "I'll do it now, have Dr. Chiang co-sign later." Never make up results that aren't back yet. Never state the diagnosis.`,
        ),
        extra: `"orders":[]`,
        after(r) {
          for (const id of strList(r.orders)) if (isOrder(id)) order(id, id === "abx" && S.bcxWarned);
        },
        cost: 1,
      });
    },
  },
  r: {
    title: tr("阿凱（R3）", "Kai (R3)"),
    sub: () =>
      S.rState === "here"
        ? tr("刀與刀之間下來門診晃一下", "Down from the OR between cases")
        : tr("回刀房了", "Back in the OR"),
    pics: ["gs_r"],
    ch: "r",
    talk: () => S.rState === "here",
    ph: tr("跟阿凱說…", "Say to Kai…"),
    async onTalk(text) {
      await chat(text, {
        ch: "r",
        think: "gs_r",
        speakers: { gs_r: tr("阿凱", "Kai") },
        body: tr(
          `你在醫學教育遊戲中扮演外科第三年住院醫師「阿凱」，30 歲，剛連開兩台刀，很累，刀與刀之間下來門診幫主任看要不要排刀。人不壞、講話快、很想把事情一次解決，習慣用流程代替判斷。玩家是外科見習醫學生（clerk）。
他的第一直覺是：膽結石＋黃疸＋發燒 → 「乾脆今天加刀把膽囊拿掉，一次解決」。clerk 如果講出「這是膽管炎，總膽管有石頭，要先引流（ERCP），不是先切膽囊」，他會停一下，承認：「對啦……膽管炎要先 ERCP，Tokyo 也是這樣寫。」clerk 如果附和加刀，他會很高興地說要去問主任。
他可以幫忙照床邊超音波（clerk 要開單或直接請他照）。
【clerk 目前知道的】
${known()}
【plan】clerk 這次的話：同意今天加刀切膽囊用 chole_now；主張先引流、先 ERCP 或指出是膽管炎用 ercp_first；其他 none。`,
          `You are playing "Kai," a third-year surgery resident, in a medical education game. He is 30, has just done two cases back to back and is exhausted; between cases he has come down to clinic to see who the attending wants booked for surgery. Not a bad guy, talks fast, wants to settle things in one go, and tends to let protocol stand in for judgment. The player is a surgery clerk (medical student).
His first instinct: gallstones + jaundice + fever -> "Let's just add him on today and take the gallbladder out, done in one go." If the clerk says "This is cholangitis, there's a stone in the CBD, it needs drainage (ERCP) first, not a cholecystectomy first," he pauses and admits: "Yeah... cholangitis gets ERCP first. That's what Tokyo says too." If the clerk goes along with adding him on, he happily says he'll go ask the attending.
He can do a bedside ultrasound (if the clerk orders one or asks him directly).
[WHAT THE CLERK KNOWS SO FAR]
${known()}
[plan] What the clerk said this time: chole_now if they agree to add on a cholecystectomy today; ercp_first if they argue for drainage first or ERCP first, or point out that it's cholangitis; otherwise none.`,
        ),
        extra: `"plan":"none"`,
        after(r) {
          if (r.plan === "chole_now" && !S.choleAgreed && !S.choleRejected) {
            S.choleAgreed = true;
            post("r", "narr", null, tr("阿凱跑進診間去問江主任。", "Kai ducks into the exam room to ask Dr. Chiang."));
            post(
              "vs",
              "npc",
              "gs_vs",
              tr(
                "加什麼刀？發燒、黃疸的人，你們有沒有想過膽管？先把他評估完再來講。",
                "Add on what? Fever and jaundice, and nobody thought about the bile duct? Finish working him up, then come talk to me.",
              ),
            );
            S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
          }
          if (r.plan === "ercp_first") S.choleRejected = true;
        },
      });
    },
  },
  vs: {
    title: tr("江主任", "Dr. Chiang"),
    sub: () => (S.vsHere ? tr("在病人旁邊", "At the bedside") : tr("診間，今天 62 號", "In clinic, 62 patients today")),
    pics: ["gs_vs"],
    ch: "vs",
    talk: true,
    ph: tr("跟江主任報告（一句話講重點）…", "Present to Dr. Chiang (bottom line first)…"),
    async onTalk(text) {
      S.vsTalked = true;
      clearWait("vsReport");
      const v = vitals();
      await chat(text, {
        ch: "vs",
        think: "gs_vs",
        tier: "default",
        speakers: { gs_vs: tr("江主任", "Dr. Chiang") },
        body: tr(
          `你在醫學教育遊戲中扮演一般外科主治「江主任」（肝膽胰），55 歲，正在看門診，今天 62 號，非常趕。玩家是見習醫學生（clerk），進診間報告預診的 12 號黃先生。
江主任話很少，要學生「一句話講重點」：第一句就要講出最重要的判斷。學生如果從主訴、現在病史慢慢講起，他會打斷：「所以呢？你覺得他是什麼？」講得好就短短一句肯定，再追問一個關鍵（「Tokyo 幾級？」「GI 打了沒？」「culture 抽了沒？」）。不直接說出診斷。
【真相】55 歲男性，已知膽結石，今天是急性膽管炎（總膽管結石，Charcot triad：發燒畏寒、黃疸、右上腹痛），目前 Tokyo Grade II，正往敗血性休克走。正確處置：收住院、血液培養後經驗性抗生素、輸液、禁食、照會消化內科做 ERCP 引流（Grade II 要早期引流），等發炎退了同次住院再做腹腔鏡膽囊切除。現在直接切膽囊是錯的。實際生命徵象：${vtxt(v)}。
【clerk 實際知道的】（講出以外的數據就追問「哪來的？」）
${known()}
${S.choleAgreed ? "阿凱剛剛跑來說要今天加刀切膽囊，你不高興。" : ""}${S.shockAt !== null ? "病人剛剛已經在門診休克了，你在處理。" : ""}
【action】accept_plan：clerk 提出膽管炎（或總膽管結石合併感染）的判斷，並提到收住院、抗生素、引流（ERCP）其中至少兩項。你同意收住院，簡短交代：抽 culture、上抗生素、輸液、NPO，GI 叫 clerk 自己打、講清楚；並給一句評語。
dismiss：clerk 什麼都還沒評估、或只說「病人要排刀」就進來，叫他出去評估完再來。
continue：其他情況。
【brevity】依 clerk 這次報告的精簡程度：good 第一句就講出判斷與關鍵數據；ok 有重點但繞；poor 流水帳或沒有判斷。`,
          `You are playing "Dr. Chiang," a general surgery attending (hepatobiliary-pancreatic), in a medical education game. He is 55, in the middle of clinic with 62 patients today, extremely rushed. The player is a clerk (medical student) coming into the exam room to present #12, Mr. Huang, whom they pre-examined.
Dr. Chiang says very little and wants students to "give me the point in one sentence": the first sentence must state the most important judgment. If the student starts slowly from the chief complaint and the HPI, he cuts in: "So? What do you think he has?" If it's good, he gives a short one-line acknowledgment, then presses on one key point ("Tokyo grade?" "Called GI yet?" "Cultures drawn?"). He does not state the diagnosis outright.
[TRUTH] 55-year-old man with known gallstones; today he has acute cholangitis (CBD stone; Charcot triad: fever with chills, jaundice, RUQ pain), currently Tokyo Grade II and heading toward septic shock. Correct management: admit, blood cultures then empiric antibiotics, IV fluids, NPO, consult GI for ERCP drainage (Grade II needs early drainage), then laparoscopic cholecystectomy during the same admission once the inflammation settles. Taking the gallbladder out right now is wrong. Actual vitals: ${vtxt(v)}.
[WHAT THE CLERK ACTUALLY KNOWS] (if they cite data beyond this, press them: "Where'd that come from?")
${known()}
${S.choleAgreed ? "Kai just came in saying he wants to add on a cholecystectomy today; you are not pleased. " : ""}${S.shockAt !== null ? "The patient has just gone into shock in clinic; you are dealing with it. " : ""}
[action] accept_plan: the clerk puts forward cholangitis (or a CBD stone with infection) as the assessment and mentions at least two of: admission, antibiotics, drainage (ERCP). You agree to admit and give brief instructions: draw cultures, start antibiotics, fluids, NPO; the clerk is to call GI themselves and make the case clearly; and add a one-line comment.
dismiss: the clerk comes in having assessed nothing, or only says "the patient is here to book surgery"; send them out to finish the workup first.
continue: anything else.
[brevity] How concise the clerk's presentation was this time: good = the first sentence states the assessment and the key data; ok = has the key points but meanders; poor = a play-by-play or no assessment.`,
        ),
        extra: `"action":"continue","brevity":"ok"`,
        after(r) {
          if (isGrade(r.brevity)) S.vsBrevity.push(r.brevity);
          const tk = tokyo();
          if (r.action === "accept_plan" && !S.vsAccepted && ptPresent()) {
            if (!(tk.a && tk.b)) {
              post(
                "vs",
                "npc",
                "gs_vs",
                tr(
                  "黃疸多少？有沒有燒？你有數字嗎？去量、去看再來。",
                  "How jaundiced? Febrile? Do you have numbers? Go measure, go look, then come back.",
                ),
              );
              S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
              return;
            }
            admit();
          } else if (r.action === "dismiss") {
            post(
              "vs",
              "narr",
              null,
              tr(
                "江主任眼睛沒離開螢幕，揮手要你出去。",
                "Without looking up from his screen, Dr. Chiang waves you out.",
              ),
            );
            S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
          }
        },
      });
    },
  },
  gi: {
    title: tr("電話：消化內科", "Phone: GI"),
    sub: () =>
      S.giCalls
        ? tr(`志豪（消化內科 R）・ERCP ${ERCP_LABEL[S.ercp]}`, `Chih-Hao (GI) · ERCP ${ERCP_LABEL[S.ercp]}`)
        : tr("照會消化內科值班 R，請他排 ERCP", "Consult the on-call GI fellow for an ERCP"),
    pics: ["gi_r"],
    ch: "gi",
    talk: () => ptPresent() || S.ptLoc === "gone",
    ph: tr(
      "用 SBAR 照會：狀況、背景、評估、建議…",
      "Consult in SBAR: situation, background, assessment, recommendation…",
    ),
    onOpen() {
      if (!S.giCalls)
        post(
          "gi",
          "narr",
          null,
          tr("你撥了消化內科值班的分機。響了五聲。", "You dial the GI on-call extension. It rings five times."),
        );
      return undefined;
    },
    async onTalk(text) {
      S.giCalls++;
      const tk = tokyo();
      const v = vitals();
      await chat(text, {
        ch: "gi",
        think: "gi_r",
        tier: "default",
        speakers: { gi_r: tr("志豪", "Chih-Hao") },
        body: tr(
          `你在醫學教育遊戲中扮演消化內科第二年住院醫師「志豪」，接到外科門診 clerk 的照會電話，要排 ERCP。他今天 ERCP 室已經排滿，手上還有兩個照會，語氣客氣但很實際：資料不齊就不會今天加做。玩家是外科見習醫學生（clerk）。
他要的是 SBAR：病人是誰、現在什麼狀況；背景（已知膽結石、慢性病、用藥）；評估（Tokyo Guidelines 2018：A 全身發炎—發燒畏寒或 WBC/CRP，B 膽汁滯留—黃疸或肝功能，C 影像—膽管擴張或看到結石；Grade II 的條件如 WBC>12,000、體溫 ≥39、T-bil ≥5；Grade III 有器官衰竭如休克、意識改變）；建議（要什麼、多急）。
clerk 講不清楚他會追問：「Tokyo 幾級？」「膽紅素多少？」「影像看到什麼？CBD 多粗？」「有沒有吃抗凝血劑或抗血小板？」「最後一餐幾點？」「INR 多少？」「病人收了嗎？誰的病人？」。clerk 講出 aspirin 他會說阿斯匹靈可以照吃、乳頭切開沒關係。
【clerk 實際知道的】（clerk 講出以外的數據就問「這個數字哪來的？」）
${known()}
【現況】${clock()}；病人實際 ${vtxt(v)}；${S.shockAt !== null ? "病人已經休克（Grade III），你會說馬上準備，血壓穩一點就推過來。" : "病人目前血壓還可以。"}${S.vsAccepted ? "江主任已經同意收住院。" : "病人還沒收住院。"}
【decision】urgent：clerk 清楚講出膽管炎的證據（發炎、黃疸或肝功能、影像看到膽管擴張或結石）並說明嚴重度，你同意今天傍晚急做；tomorrow：資料大致有但嚴重度講不清楚，或沒有影像，你說先抗生素、排明天第一台；none：資料太少，叫他補齊再打來。
【sbar】依 clerk 這次照會的完整與清楚程度：good、ok、poor。`,
          `You are playing "Chih-Hao," a second-year GI fellow, in a medical education game. He is taking a consult call from the clerk in the surgery clinic asking for an ERCP. His ERCP list is already full today and he has two other consults waiting; he is polite but practical: if the information is incomplete, he won't add the case on today. The player is a surgery clerk (medical student).
He wants SBAR: who the patient is and what is going on now; background (known gallstones, chronic conditions, medications); assessment (Tokyo Guidelines 2018: A, systemic inflammation: fever/chills or WBC/CRP; B, cholestasis: jaundice or LFTs; C, imaging: ductal dilation or a visible stone; Grade II criteria such as WBC >12,000, temp >=39, T-bil >=5; Grade III means organ dysfunction such as shock or altered mental status); recommendation (what they want, how urgently).
If the clerk is unclear he presses: "Tokyo grade?" "What's the bilirubin?" "What did imaging show? How wide is the CBD?" "Any anticoagulants or antiplatelets?" "When was his last meal?" "What's the INR?" "Has he been admitted? Whose patient is he?" If the clerk mentions aspirin, he says the aspirin can be continued, it's fine for a sphincterotomy.
[WHAT THE CLERK ACTUALLY KNOWS] (if the clerk cites data beyond this, ask "Where's that number from?")
${known()}
[CURRENT SITUATION] ${clock()}; patient's actual vitals ${vtxt(v)}; ${S.shockAt !== null ? "The patient is already in shock (Grade III); you say you'll get ready right away and to bring him over as soon as his pressure is a bit steadier. " : "The patient's blood pressure is still OK for now. "}${S.vsAccepted ? "Dr. Chiang has already agreed to admit." : "The patient hasn't been admitted yet."}
[decision] urgent: the clerk clearly lays out the evidence for cholangitis (inflammation, jaundice or LFTs, imaging showing ductal dilation or a stone) and explains the severity; you agree to do it urgently this evening. tomorrow: the data are mostly there but the severity isn't made clear, or there is no imaging; you say start antibiotics and you'll put him first on tomorrow's list. none: too little information; tell them to fill in the gaps and call back.
[sbar] How complete and clear the clerk's consult was this time: good, ok, poor.`,
        ),
        extra: `"decision":"none","sbar":"ok"`,
        after(r) {
          if (isGrade(r.sbar)) S.giSbar.push(r.sbar);
          let d = r.decision;
          if (S.shockAt !== null && d !== "none") d = "urgent";
          if (d === "urgent" && !(tk.a && tk.b && tk.c) && S.shockAt === null) {
            post(
              "gi",
              "npc",
              "gi_r",
              tk.c
                ? tr(
                    "膽紅素、發炎指數有了再講好不好？",
                    "Can we talk once you have the bilirubin and inflammatory markers?",
                  )
                : tr(
                    "影像呢？沒有影像我不知道是不是真的膽道阻塞。超音波照了再打給我。",
                    "What about imaging? Without it I can’t tell if the duct is really blocked. Get an ultrasound, then call me back.",
                  ),
            );
            if (S.ercp === "none" && tk.a && tk.b) d = "tomorrow";
            else return;
          }
          if (d === "urgent" && S.ercp !== "urgent") {
            S.ercp = "urgent";
            S.ercpAt = S.t;
            eventBanner(tr("ERCP 排定", "ERCP booked"), tr("今天 17:30 急做", "Urgent, today 17:30"));
          } else if (d === "tomorrow" && S.ercp === "none") {
            S.ercp = "tomorrow";
            S.ercpAt = S.t;
            post(
              "gi",
              "narr",
              null,
              tr("志豪把黃先生排在明天早上第一台。", "Chih-Hao puts Mr. Huang first on tomorrow morning’s list."),
            );
          }
        },
        cost: 2,
      });
    },
  },
  triage: {
    title: tr("分診單", "Triage slip"),
    sub: tr("夾在生命徵象量測台上", "Clipped to the vitals station"),
    ch: "triage",
    doc: doc(() => {
      S.triageRead = true;
      return [
        [
          tr("12 號　黃○○　55 歲男", "#12 · Huang · M, 55"),
          tr(
            "主訴：上個月超音波看到膽結石，來排手術。",
            "CC: gallstones on ultrasound last month; here to schedule surgery.",
          ),
        ],
        [
          tr("13:40 生命徵象", "13:40 vitals"),
          tr(
            `耳溫 ${TRIAGE.temp.toFixed(1)}°C（？）　BP ${TRIAGE.sbp}/${TRIAGE.dbp}　HR ${TRIAGE.hr}　RR ${TRIAGE.rr}`,
            `Ear temp ${TRIAGE.temp.toFixed(1)}°C (?) · BP ${TRIAGE.sbp}/${TRIAGE.dbp} · HR ${TRIAGE.hr} · RR ${TRIAGE.rr}`,
          ),
        ],
        [
          tr("分診備註", "Triage note"),
          tr(
            "病人說剛在外面曬太陽。體溫旁邊用原子筆畫了一個問號，沒有其他標註。",
            "Patient says he was just out in the sun. A question mark in ballpoint next to the temperature; nothing else noted.",
          ),
        ],
      ];
    }),
  },
  emr: {
    title: tr("病歷", "Chart"),
    sub: tr("黃○○・上個月的健檢與門診紀錄", "Huang · last month’s health check and clinic notes"),
    ch: "emr",
    doc: doc(() => {
      S.emrRead = true;
      return [
        [tr("基本資料", "Demographics"), tr("黃○○，55 歲男性，計程車司機。", "Huang, 55-year-old man, taxi driver.")],
        [
          tr("過去病史", "PMH"),
          tr(
            "高血壓（Amlodipine 5 mg）；三年前冠狀動脈支架，長期服用 Aspirin 100 mg。無藥物過敏。",
            "Hypertension (amlodipine 5 mg); coronary stent three years ago, on long-term aspirin 100 mg. NKDA.",
          ),
        ],
        [
          tr("上月健檢腹部超音波", "Abd US, health check last month"),
          tr(
            "膽囊多顆結石，最大 1.8 cm；膽囊壁不厚；總膽管 6 mm。肝臟輕度脂肪肝。",
            "Multiple gallstones, largest 1.8 cm; gallbladder wall not thickened; CBD 6 mm. Mild fatty liver.",
          ),
        ],
        [
          tr("上月抽血", "Labs, last month"),
          tr(
            "AST 28，ALT 31，T-bil 0.7，ALP 82，Cr 0.9，WBC 6,800。",
            "AST 28, ALT 31, T-bil 0.7, ALP 82, Cr 0.9, WBC 6,800.",
          ),
        ],
        [
          tr("上月門診（江主任）", "Clinic, last month (Dr. Chiang)"),
          tr(
            "有症狀的膽結石（餐後右上腹痛兩次）。建議擇期腹腔鏡膽囊切除，病人說要回去排工作，本月回診排刀。",
            "Symptomatic cholelithiasis (two episodes of postprandial RUQ pain). Elective lap chole recommended; patient wants to sort out his work schedule first and will return this month to book surgery.",
          ),
        ],
      ];
    }),
  },
};

/* ---------- level ---------- */

const drawIvPole: DrawFn = (c, px, py) => {
  R(c, "#9aa0b2", px + 7, py - 6, 2, 20);
  R(c, "#7a8092", px + 3, py + 13, 10, 2);
  obox(c, "#e6f2f8", px + 9, py - 6, 5, 8);
  R(c, "#c9dce8", px + 9, py - 1, 5, 2);
};

const drawPoster: DrawFn = (c, px, py) => {
  obox(c, "#f4f4f6", px + 2, py + 2, 12, 11);
  R(c, "#3b8a5a", px + 3, py + 3, 10, 2);
  R(c, "#e0b860", px + 6, py + 7, 2, 2);
  R(c, "#e0b860", px + 9, py + 8, 2, 2);
};

export const GS3: Level = {
  id: "gs-opd",
  no: tr("三", "3"),
  short: tr("第三關　外科門診", "Level 3 · Surgery Clinic"),
  title: tr("門診：只是來排刀", "Clinic: Just Here to Book Surgery"),
  place: tr("外科門診", "Surgery clinic"),
  blurb: tr(
    "上個月看到膽結石，今天來排刀。他說臉黃是曬太陽曬的。",
    "Gallstones on last month’s scan; today he’s here to book surgery. He says the yellow is from the sun.",
  ),
  cast: ["huang", "huang_wife", "opd_nurse", "gs_vs", "gs_r", "gi_r"],
  intro: [
    tr(
      "週三下午 14:00，江主任的一般外科門診，今天掛了 62 號。你負責先幫病人預診，問完進診間跟主任報告；主任最討厭聽流水帳。",
      "Wednesday, 14:00: Dr. Chiang’s general surgery clinic, 62 patients booked today. Your job is to see each patient first, then go into the exam room and present to him. He hates a play-by-play.",
    ),
    tr(
      "12 號黃先生，55 歲計程車司機。上個月健檢看到膽結石，今天來「排膽囊手術的日期」。他已經坐在預診室，太太在外面候診區。",
      "#12, Mr. Huang, a 55-year-old taxi driver. A health check last month found gallstones, and today he’s here to “set a date for his gallbladder surgery.” He’s already sitting in the pre-exam room; his wife is out in the waiting area.",
    ),
  ],
  start: 14 * 60,
  maxT: 85,
  night: false,
  map: [
    "####################",
    "#aaaa#bbbbb#ccccccc#",
    "#aaaa#bbbbb#ccccccc#",
    "#aaaa#bbbbb#ccccccc#",
    "##a#####b#####c#####",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "#wwwwwwwwwwwwwwwwww#",
    "####################",
  ],
  zones: {
    a: ["#e3eaf6", "#ccd6ea", "#d6dff0"],
    b: ["#e6e8f2", "#cfd3e4", "#dcdfec"],
    c: ["#e3efe4", "#cadfcc", "#d6e7d8"],
    w: ["#ece6d6", "#d9d0bb", "#e2dac7"],
  },
  windows: [
    [3, 0],
    [8, 0],
    [16, 0],
  ],
  exits: [DOOR],
  chName: {
    pt: tr("黃先生", "Mr. Huang"),
    wife: tr("黃太太", "Mrs. Huang"),
    nurse: tr("淑惠姐", "Shu-Hui"),
    vs: tr("江主任", "Dr. Chiang"),
    r: tr("阿凱", "Kai"),
    gi: tr("消化內科", "GI"),
    triage: tr("分診單", "Triage slip"),
    emr: tr("病歷", "Chart"),
    sys: tr("門診", "Clinic"),
  },
  objects: [
    { id: "desk1", type: "desk", x: 2, y: 1, w: 2 },
    { id: "emr", type: "computer", x: 3, y: 1, label: tr("電腦：病歷與上月超音波", "PC: chart & last month’s US") },
    { id: "chair1", type: "chair", x: 1, y: 1 },
    { id: "desk2", type: "desk", x: 7, y: 2, w: 2, label: tr("江主任的診桌", "Dr. Chiang’s desk"), act: "vs" },
    { id: "pc2", type: "computer", x: 7, y: 2 },
    { id: "exam2", type: "exambed", x: 10, y: 1, h: 2 },
    { id: "stretcher", type: "bed", x: 13, y: 1, h: 2 },
    { id: "monitor", type: "monitorStand", x: 14, y: 1, label: tr("監視器", "Monitor"), act: "mon" },
    { id: "ivpole", type: "ivpole", x: 15, y: 1 },
    { id: "us", type: "us", x: 16, y: 1 },
    { id: "crash", type: "cart", x: 18, y: 1, label: tr("急救車", "Crash cart") },
    { id: "shelf", type: "shelf2", x: 18, y: 2 },
    { id: "chairsA", type: "chairs", x: 2, y: 7, w: 7 },
    { id: "chairsB", type: "chairs", x: 2, y: 9, w: 7 },
    {
      id: "counter",
      type: "counter",
      x: 14,
      y: 7,
      w: 4,
      label: tr("跟診護理站", "Clinic nurse station"),
      act: "nurse",
    },
    { id: "phone", type: "phone", x: 17, y: 7, label: tr("電話：照會消化內科", "Phone: GI consult"), act: "gi" },
    {
      id: "triage",
      type: "bp",
      x: 18,
      y: 8,
      label: tr("分診量測台（分診單）", "Triage station (slip)"),
      act: "triage",
    },
    { id: "water", type: "water", x: 1, y: 5 },
    { id: "poster", type: "poster", x: 5, y: 4 },
    { id: "vend", type: "vending", x: 18, y: 10 },
    { id: "plant", type: "plant", x: 12, y: 10 },
    { id: "wc", type: "wcdoor", x: 0, y: 9, label: tr("廁所", "Restroom") },
  ],
  furniture: { ivpole: drawIvPole, poster: drawPoster },
  labels: {
    pt: tr("黃先生", "Mr. Huang"),
    wife: tr("黃太太", "Mrs. Huang"),
    nurse: tr("淑惠姐", "Shu-Hui"),
    vs: tr("江主任", "Dr. Chiang"),
    r: tr("阿凱", "Kai"),
  },
  help: {
    pt: tr("問診、身體檢查（會打開特寫）；休克時的處置", "History, physical exam (opens a close-up); shock management"),
    wife: tr("黃太太：知道一些先生沒講的事", "Mrs. Huang: knows things her husband isn’t saying"),
    emr: tr("上個月的超音波、抽血、門診紀錄、過去病史", "Last month’s US, labs, clinic note, past history"),
    triage: tr("13:40 分診時量的生命徵象", "Vitals taken at 13:40 triage"),
    vs: tr(
      "江主任在診間看診，進去報告，一句話講重點",
      "Dr. Chiang is seeing patients; go in and present, bottom line first",
    ),
    nurse: tr("抽血、培養、超音波、CT、IV、輸液、抗生素", "Labs, cultures, US, CT, IV, fluids, antibiotics"),
    gi: tr("打給消化內科排 ERCP（用 SBAR）", "Call GI to book an ERCP (use SBAR)"),
    mon: tr("治療室監視器（病人躺上推床才有用）", "Treatment-room monitor (works once he’s on the stretcher)"),
    r: tr("阿凱，刀與刀之間會下來一下", "Kai, drops by between cases"),
    crash: tr("急救車", "Crash cart"),
    wc: tr("廁所", "Restroom"),
  },
  helpRules: [
    tr(
      "你是預診的人：問完、檢查完，再進診間向江主任報告。第一句話就講你的判斷。",
      "You see the patient first: take the history, examine him, then go in and present to Dr. Chiang. Lead with your assessment.",
    ),
    tr(
      "病人說來排刀，不代表他今天只需要排刀。分診單、太太、舊病歷都可能告訴你更多。",
      "He says he’s here to book surgery. That doesn’t mean booking surgery is all he needs today. The triage slip, his wife and the old chart may tell you more.",
    ),
    tr(
      "照會別科要講 SBAR：狀況、背景、評估、建議。資料不齊，別人不會幫你插隊。",
      "Consult other services in SBAR: situation, background, assessment, recommendation. Without the data, nobody will squeeze you in.",
    ),
  ],
  player: [3, 3],
  ents: () => [
    mkEnt({ k: "huang", id: "huang", x: PRE[0], y: PRE[1], act: "pt", dir: "left", face: "left" }),
    mkEnt({ k: "huang_wife", id: "huang_wife", x: 7, y: 9, act: "wife" }),
    mkEnt({ k: "opd_nurse", id: "opd_nurse", x: 15, y: 6, act: "nurse" }),
    mkEnt({ k: "gs_vs", id: "gs_vs", x: 8, y: 1, act: "vs" }),
  ],
  init: (): Vars => ({
    revealed: new Set(),
    orders: {},
    vitalsSeen: [],
    triageRead: false,
    emrRead: false,
    ptLoc: "pre",
    wcBack: null,
    awayAt: null,
    toldStay: false,
    npoTold: false,
    bcxWarned: false,
    abxBeforeCx: false,
    fluidAt: null,
    abxAt: null,
    apapAt: null,
    rigor: false,
    shockAt: null,
    helpCalled: false,
    shockActs: [],
    resusFluid: false,
    o2: false,
    vsTalked: false,
    vsAccepted: false,
    acceptAt: null,
    vsBrevity: [],
    vsComing: null,
    vsHere: false,
    rState: "none",
    choleAgreed: false,
    choleRejected: false,
    giCalls: 0,
    giSbar: [],
    ercp: "none",
    ercpAt: null,
    transportAt: null,
    endAt: null,
    wifeAsked: false,
  }),
  dialogs,
  zooms: { pt: ptZoom },
  quick: {
    mon: openMon,
    crash: () =>
      note(
        tr(
          "急救車：氧氣面罩、Ambu、IV 用品、輸液、急救藥物都在這裡。",
          "Crash cart: O₂ masks, Ambu bag, IV supplies, fluids and code drugs are all here.",
        ),
      ),
    wc: () =>
      note(
        tr(
          "廁所。清潔阿姨說剛剛有個先生在裡面吐，馬桶旁邊的尿顏色好深。",
          "Restroom. The cleaning lady says a man was just throwing up in here, and the urine by the toilet was really dark.",
        ),
      ),
  },

  onStart: () =>
    eventBanner(
      tr("預診開始", "Pre-exam begins"),
      tr("江主任一般外科門診・今天 62 號", "Dr. Chiang’s GS clinic · 62 patients today"),
    ),
  onStep(x, y) {
    if (zone(x, y) === "b" && !S.vsTalked && S.t < 6)
      post("vs", "npc", "gs_vs", tr("12 號預診完了再進來。", "Finish #12’s pre-exam, then come in."));
  },
  tick(t) {
    if (t === 1)
      post(
        "nurse",
        "npc",
        "opd_nurse",
        tr(
          "學弟，12 號黃先生是上個月健檢看到膽結石轉來排刀的，你先幫他預診。主任今天 62 號，進去報告講重點喔。",
          "#12, Mr. Huang, was sent to book surgery after a health check found gallstones last month. Do his pre-exam first. Dr. Chiang has 62 today, so keep your presentation to the point.",
        ),
      );
    if (t === 9 && S.ptLoc === "pre")
      post(
        "nurse",
        "npc",
        "opd_nurse",
        tr(
          "預診室下一號要用喔，問完先請他到外面等。",
          "We need the pre-exam room for the next patient. When you’re done, have him wait outside.",
        ),
      );
    if (t === 11 && S.ptLoc === "pre") {
      post(
        "pt",
        "narr",
        null,
        tr(
          "黃先生被請到外面候診區，坐在太太旁邊。",
          "Mr. Huang is sent out to the waiting area and sits down next to his wife.",
        ),
      );
      toWaiting();
    }
    if (t === 14 && !S.vsTalked && !S.vsAccepted)
      ask("vs", "gs_vs", tr("12 號呢？", "What about #12?"), {
        id: "vsReport",
        text: tr("江主任在等你報告 12 號", "Dr. Chiang is waiting for you to present #12"),
        dlg: "vs",
      });
    if (t === 16 && S.rState === "none") {
      S.rState = "here";
      spawnAt("gs_r", "r", DOOR, [11, 6]);
      post(
        "r",
        "npc",
        "gs_r",
        tr(
          "學弟，主任說今天有一個膽結石要排刀？我剛下刀，有空檔。",
          "Hey, Dr. Chiang says there’s a gallstone case to book today? I just finished a case, I’ve got a gap.",
        ),
      );
    }
    if (t === 19 && S.rState === "here" && !S.choleRejected && !S.vsAccepted && S.shockAt === null)
      ask(
        "r",
        "gs_r",
        tr(
          "我看了一下，膽結石嘛。主任下午刀房有空檔，乾脆今天加刀把膽囊拿掉，一次解決，你覺得呢？",
          "I took a look. Gallstones, right? Dr. Chiang has an open OR slot this afternoon. Let’s just add him on and take the gallbladder out today, done in one go. What do you think?",
        ),
        {
          id: "rq",
          text: tr("阿凱問要不要今天加刀", "Kai wants to add on surgery today"),
          dlg: "r",
        },
      );
    if (t === 44 && S.rState === "here") {
      S.rState = "left";
      clearWait("rq");
      post(
        "r",
        "npc",
        "gs_r",
        tr(
          "刀房 call 我了，下一台要開始。有事打給我。",
          "The OR’s calling me, next case is starting. Call me if you need anything.",
        ),
      );
      const r = findEnt("gs_r");
      if (r && !r.hidden) leaveBy(r, DOOR);
    }
    if (t === 20 && !S.wifeAsked && S.ptLoc !== "away" && S.shockAt === null && !S.vsAccepted)
      ask(
        "wife",
        "huang_wife",
        tr(
          "醫生……我先生昨天晚上抖到整張床都在搖，這樣要緊嗎？他叫我不要講。",
          "Doctor… last night my husband was shaking so hard the whole bed rattled. Is that serious? He told me not to say anything.",
        ),
        {
          id: "wifeq",
          text: tr("黃太太有話想跟你說", "Mrs. Huang wants a word with you"),
          dlg: "wife",
        },
      );
    if (t === 23 && ptPresent() && S.ptLoc !== "tx" && S.shockAt === null && !S.vsAccepted && !S.toldStay)
      ask(
        "pt",
        "huang",
        tr(
          "醫生，刀排好了沒？我四點要去載一個固定的客人，先讓我走，晚上再打電話問好不好？",
          "Doc, is the surgery booked yet? I’ve got a regular passenger to pick up at four. Let me go for now and I’ll call tonight, okay?",
        ),
        {
          id: "leave",
          text: tr("黃先生想先離開去開車", "Mr. Huang wants to leave and go drive"),
          dlg: "pt",
          untilT: 33,
        },
      );
    if (t === 33 && ptPresent() && S.ptLoc !== "tx" && S.shockAt === null && !S.vsAccepted && !S.toldStay) {
      if (Object.keys(S.orders).length === 0) {
        post(
          "pt",
          "narr",
          null,
          tr(
            "沒有人回答他。黃先生等得不耐煩，拿著鑰匙站起來。",
            "Nobody answers him. Tired of waiting, Mr. Huang gets up, keys in hand.",
          ),
        );
        leave();
      } else
        post(
          "pt",
          "npc",
          "huang",
          tr(
            "抽血要等那麼久喔……好啦好啦，我再等一下。",
            "Blood work takes this long, huh… Fine, fine, I’ll wait a bit longer.",
          ),
        );
    }
    if (t === 25 && !S.rigor && ptPresent() && S.shockAt === null) {
      S.rigor = true;
      eventBanner(
        tr("寒顫", "Rigors"),
        S.ptLoc === "tx" ? tr("治療室", "Treatment room") : tr("候診區", "Waiting area"),
      );
      post(
        "pt",
        "alert",
        "huang_wife",
        tr("醫生！他又在發抖了，牙齒都在打架！", "Doctor! He’s shaking again, his teeth are chattering!"),
      );
    }
    if (S.wcBack && t >= S.wcBack.t && S.ptLoc === "wc" && S.shockAt === null) {
      const back = S.wcBack.loc;
      S.wcBack = null;
      post(
        "nurse",
        "npc",
        "opd_nurse",
        tr("尿杯拿到了。顏色好深，像烏龍茶。", "Got the urine cup. It’s really dark, like oolong tea."),
      );
      if (back === "pre") {
        S.ptLoc = "pre";
        walkEnt(pt(), PRE);
      } else {
        S.ptLoc = "wait";
        walkEnt(pt(), WAIT);
      }
    }
    for (const [k, o] of Object.entries(S.orders) as [OrderId, Order][])
      if (!o.done && t >= o.ready && (ptPresent() || k === "bcx")) deliver(k);
    if (S.ptLoc === "away" && t >= shockMinute()) {
      endD();
      return;
    }
    if (S.shockAt === null && ptPresent() && t >= shockMinute()) shock();
    if (S.shockAt !== null && !S.helpCalled && t === S.shockAt + 2) {
      post("pt", "narr", null, tr("黃太太的叫聲把淑惠姐引來了。", "Mrs. Huang’s cries bring Shu-Hui running."));
      helpArrives();
    }
    if (S.vsComing === t && !S.vsHere) {
      S.vsHere = true;
      const p = pt();
      walkEnt(ent("gs_vs"), [p.x + 1 <= 18 ? p.x + 1 : p.x - 1, p.y]);
      post(
        "pt",
        "npc",
        "gs_vs",
        S.resusFluid
          ? tr(
              "輸液上了？culture、抗生素？好。Levophed 準備，GI 我來打。",
              "Fluids in? Cultures, antibiotics? Good. Get Levophed ready. I’ll call GI.",
            )
          : tr(
              "兩條 IV，LR 一千灌進去，culture 抽完抗生素馬上打。GI 我來打，叫他們準備 ERCP。",
              "Two IVs, a liter of LR wide open, cultures and then antibiotics right away. I’ll call GI and tell them to get ready for ERCP.",
            ),
      );
      S.resusFluid = true;
      S.fluidAt ??= t;
      S.abxAt ??= t;
      S.ercp = "urgent";
      S.endAt = t + 4;
    }
    if (S.vsAccepted && S.acceptAt !== null && S.shockAt === null) {
      if (t === S.acceptAt + 8 && S.ercp === "none")
        ask(
          "vs",
          "gs_vs",
          tr("GI 打了沒？他要引流，不是只有抗生素。", "Called GI yet? He needs drainage, not just antibiotics."),
          {
            id: "giq",
            text: tr("江主任問你照會消化內科了沒", "Dr. Chiang asks if you’ve consulted GI"),
            dlg: "gi",
          },
        );
      if (S.transportAt === null && (S.ercp !== "none" || t >= S.acceptAt + 16)) {
        S.transportAt = t + 3;
        post(
          "nurse",
          "npc",
          "opd_nurse",
          tr("病房 7 西有床了，傳送三分鐘到。", "There’s a bed on 7 West. Transport will be here in three minutes."),
        );
      }
    }
    if (S.transportAt === t && S.shockAt === null) transport();
    if (S.endAt === t) {
      if (S.shockAt !== null) endC();
      else if (S.ercp !== "none") endA();
      else endB();
    }
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        {
          who: "opd_nurse",
          text: tr(
            "學弟，主任說 12 號他自己處理，你去看下一號。",
            "Dr. Chiang says he’ll handle #12 himself. Go see the next one.",
          ),
        },
      ],
      tr("時間到了，主任接手。", "Time’s up. The attending takes over."),
    ),

  hud() {
    const last = S.vitalsSeen.at(-1);
    return [
      [
        last ? tr(`${clock(last.t)} 你量的`, `Yours, ${clock(last.t)}`) : tr("生命徵象", "Vitals"),
        last
          ? last.what === "temp"
            ? `${last.v.temp.toFixed(1)}°C`
            : tr(`${last.v.sbp}/${last.v.dbp}　HR ${last.v.hr}`, `${last.v.sbp}/${last.v.dbp} HR ${last.v.hr}`)
          : S.triageRead
            ? tr("分診 38.4°C（？）", "Triage 38.4°C (?)")
            : tr("還沒量", "Not taken"),
      ],
      ["ERCP", ERCP_LABEL[S.ercp]],
    ];
  },
  urgent: () => S.shockAt !== null || preShock(),
  outcomeText: () =>
    S.shockAt !== null
      ? tr("門診休克後送急診、緊急 ERCP", "Shock in clinic, to ER, emergency ERCP")
      : S.vsAccepted
        ? tr("主任收住院", "Admitted by attending")
        : tr("還沒有處置", "No management yet"),

  drawBelow(c, now) {
    const px = 14 * T;
    const py = 1 * T;
    c.fillStyle = "#8a90a2";
    c.fillRect(px + 7, py + 6, 2, 10);
    c.fillStyle = "#3a3f52";
    c.fillRect(px + 1, py, 14, 9);
    c.fillStyle = "#0f1f1a";
    c.fillRect(px + 2, py + 1, 12, 7);
    if (S.ptLoc === "tx" || S.shockAt !== null) {
      const v = vitals();
      const fast = v.hr > 115;
      c.fillStyle = v.sbp < 90 && Math.floor(now / 400) % 2 ? "#ff3b4e" : "#5be38a";
      for (let i = 0; i < 12; i++) {
        const ph = (i + Math.floor(now / (fast ? 60 : 110))) % 12;
        c.fillRect(px + 2 + i, ph === 3 ? py + 2 : ph === 4 ? py + 6 : py + 4, 1, 1);
      }
    }
  },

  graderCase: tr(
    `病例：55 歲男性計程車司機，上個月健檢發現膽結石，今天到外科門診「來排刀」。真相：急性膽管炎（總膽管結石），Charcot triad：昨晚發燒畏寒 39.2 度（早上吃了普拿疼所以分診只有 38.4，分診單上畫了問號但沒人處理）、黃疸（說是曬的）、右上腹痛；尿像濃茶、大便變淡、皮膚癢。Tokyo Guidelines 2018：WBC 16,800、CRP 15.6、T-bil 4.8、超音波總膽管 11 mm 併遠端結石，屬 Grade II。病人淡化症狀、想趕快回去開車，叫太太不要講發燒；太太會主動找 clerk。約 23 分鐘時病人要求先離開，同意就會在外面休克被送回急診。若沒有在約 34 分鐘內給輸液與抗生素，約 42 分鐘進展成 Reynolds pentad（意識混亂、低血壓）。陷阱：R3 阿凱提議「今天加刀把膽囊拿掉」——膽管炎要先引流（ERCP），等發炎退了才切膽囊。抗生素前要先抽血液培養（護理師會提醒一次）。正確處置：認出膽管炎、量體溫與生命徵象、抽血（CBC/CRP、肝功能與膽紅素、腎功能、lipase、lactate、凝血）、血液培養、影像（超音波或 CT）、IV 輸液、經驗性抗生素、禁食、用一句話向主任報告並收住院、用 SBAR 照會消化內科排早期 ERCP（提到 aspirin、最後一餐）。休克時：叫人、平躺、兩條大號 IV 快速輸液、培養後立即抗生素、給氧、評估意識與血糖。`,
    `Case: 55-year-old male taxi driver; gallstones found on a health check last month; came to the surgery clinic today "to book surgery." Truth: acute cholangitis (CBD stone). Charcot triad: fever with chills last night, 39.2 (took Panadol/acetaminophen in the morning, so triage showed only 38.4; the triage slip had a question mark but nobody followed up), jaundice (he says it's a tan), RUQ pain; tea-colored urine, pale stools, itchy skin. Tokyo Guidelines 2018: WBC 16,800, CRP 15.6, T-bil 4.8, ultrasound CBD 11 mm with a distal stone: Grade II. The patient downplays his symptoms, wants to get back to driving, and told his wife not to mention the fever; the wife will seek out the clerk. Around minute 23 the patient asks to leave; if allowed, he goes into shock outside and is brought back to the ER. If fluids and antibiotics are not given by about minute 34, he progresses to Reynolds pentad (confusion, hypotension) at about minute 42. Trap: the R3, Kai, proposes "add him on today and take the gallbladder out"; cholangitis needs drainage (ERCP) first, and the gallbladder comes out only after the inflammation settles. Blood cultures must be drawn before antibiotics (the nurse reminds once). Correct management: recognize cholangitis; take temperature and vitals; labs (CBC/CRP, LFTs and bilirubin, renal function, lipase, lactate, coags); blood cultures; imaging (ultrasound or CT); IV fluids; empiric antibiotics; NPO; present to the attending in one sentence and admit; consult GI in SBAR for early ERCP (mentioning aspirin and the last meal). In shock: call for help, lay him flat, two large-bore IVs with rapid fluids, cultures then immediate antibiotics, oxygen, assess mental status and glucose.`,
  ),
  rubric: [
    tr(
      "發現發燒與分診單的問號，並自己重量生命徵象",
      "Noticed the fever and the question mark on the triage slip, and rechecked vitals themselves",
    ),
    tr(
      "問出 Charcot triad 與相關病史（畏寒、茶色尿、灰白便、皮膚癢、吃過普拿疼），包含從太太取得的資訊",
      "Elicited the Charcot triad and related history (chills, tea-colored urine, pale stools, itching, Panadol taken), including information from the wife",
    ),
    tr(
      "身體檢查：鞏膜黃疸、右上腹壓痛、皮膚、意識",
      "Physical exam: scleral icterus, RUQ tenderness, skin, mental status",
    ),
    tr(
      "檢查：發炎指數、肝功能與膽紅素、影像（總膽管）、血液培養在抗生素之前",
      "Workup: inflammatory markers, LFTs and bilirubin, imaging (CBD), blood cultures before antibiotics",
    ),
    tr(
      "早期處置：IV 輸液、經驗性抗生素、禁食；不讓病人離開",
      "Early management: IV fluids, empiric antibiotics, NPO; did not let the patient leave",
    ),
    tr(
      "向江主任一句話講重點，並拒絕「今天先切膽囊」的建議",
      "Presented to Dr. Chiang with the bottom line first, and rejected the 'take the gallbladder out today' suggestion",
    ),
    tr(
      "用 SBAR 照會消化內科，講出 Tokyo 分級證據，爭取早期 ERCP",
      "Consulted GI in SBAR, citing the Tokyo grading evidence, and secured an early ERCP",
    ),
    tr("休克的預防或處置", "Prevention or management of shock"),
  ],
  facts: () => [
    tr(`分診單：${S.triageRead ? "看過" : "沒看"}`, `Triage slip: ${S.triageRead ? "read" : "not read"}`),
    tr(
      `自己量體溫：${S.vitalsSeen.filter((v) => v.what !== "bp").length} 次；血壓：${S.vitalsSeen.filter((v) => v.what !== "temp").length} 次`,
      `Temps taken by clerk: ${S.vitalsSeen.filter((v) => v.what !== "bp").length}; BPs: ${S.vitalsSeen.filter((v) => v.what !== "temp").length}`,
    ),
    tr(
      `問出的病史：${S.revealed.size} 項${S.revealed.has("fever") ? "（含發燒）" : "（沒問出發燒）"}`,
      `History items obtained: ${S.revealed.size}${S.revealed.has("fever") ? " (including fever)" : " (fever not elicited)"}`,
    ),
    tr(
      `血液培養：${S.orders.bcx ? (S.abxBeforeCx ? "抗生素之後才抽" : "有，在抗生素之前") : "沒抽"}`,
      `Blood cultures: ${S.orders.bcx ? (S.abxBeforeCx ? "drawn only after antibiotics" : "yes, before antibiotics") : "not drawn"}`,
    ),
    tr(
      `影像：${S.orders.us ? "超音波" : ""}${S.orders.ct ? " CT" : ""}${!S.orders.us && !S.orders.ct ? "沒有" : ""}`,
      `Imaging: ${S.orders.us ? "ultrasound" : ""}${S.orders.ct ? " CT" : ""}${!S.orders.us && !S.orders.ct ? "none" : ""}`,
    ),
    tr(
      `輸液：${S.fluidAt !== null ? clock(S.fluidAt) : "沒有"}；抗生素：${S.abxAt !== null ? clock(S.abxAt) : "沒有"}`,
      `IV fluids: ${S.fluidAt !== null ? clock(S.fluidAt) : "none"}; antibiotics: ${S.abxAt !== null ? clock(S.abxAt) : "none"}`,
    ),
    tr(
      `讓病人離開：${S.awayAt !== null ? `是（${clock(S.awayAt)}）` : "否"}`,
      `Let the patient leave: ${S.awayAt !== null ? `yes (${clock(S.awayAt)})` : "no"}`,
    ),
    tr(
      `阿凱提議今天加刀：${S.choleAgreed ? "clerk 附和" : S.choleRejected ? "clerk 指出要先引流" : "沒有表態"}`,
      `Kai proposed adding on surgery today: ${S.choleAgreed ? "clerk went along" : S.choleRejected ? "clerk said drain first" : "clerk took no position"}`,
    ),
    tr(
      `向主任報告：${S.vsBrevity.length ? S.vsBrevity.join("、") : "沒有"}；收住院：${S.acceptAt !== null ? clock(S.acceptAt) : "沒有"}`,
      `Presented to attending: ${S.vsBrevity.length ? S.vsBrevity.join(", ") : "no"}; admitted: ${S.acceptAt !== null ? clock(S.acceptAt) : "no"}`,
    ),
    tr(
      `照會消化內科：${S.giCalls ? `${S.giCalls} 次（SBAR ${S.giSbar.join("、") || "?"}），ERCP ${ERCP_LABEL[S.ercp]}` : "沒有"}`,
      `GI consult: ${S.giCalls ? `${S.giCalls} call(s) (SBAR ${S.giSbar.join(", ") || "?"}), ERCP ${ERCP_LABEL[S.ercp]}` : "none"}`,
    ),
    tr(
      `休克：${S.shockAt !== null ? `有（${clock(S.shockAt)}）` : "沒有"}`,
      `Shock: ${S.shockAt !== null ? `yes (${clock(S.shockAt)})` : "no"}`,
    ),
  ],
  known,
};
